import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

const translations = {
  en: {
    'header.home': 'Home',
    'header.flights': 'Flights',
    'header.hotels': 'Hotels',
    'header.packages': 'Packages',
    'header.saved': 'Saved',
    'header.profile': 'Profile',
    'header.signIn': 'Sign In',
    'header.signOut': 'Sign Out',
    'header.smartAssistant': 'Smart Travel Assistant',
    'hero.title': 'Your Personal',
    'hero.aiAssistant': 'AI Travel Assistant',
    'hero.subtitle': 'for Every Journey',
    'hero.description': 'Plan trips with an AI travel assistant, discover flight and hotel options, and build multi-city routes. Confirm availability and final prices directly with our travel partners.',
    'hero.budgetFriendly': 'Budget-Friendly',
    'hero.midRange': 'Mid-Range',
    'hero.luxury': 'Luxury',
    'hero.aiPowered': 'AI-Powered',
    'hero.aiPoweredDesc': 'Smart recommendations tailored to your preferences',
    'hero.globalCoverage': 'Global Coverage',
    'hero.globalCoverageDesc': 'Access to flights and hotels worldwide',
    'hero.support247': 'Fast Support',
    'hero.support247Desc': 'Quick, personal assistance when you need it',
    'hero.secure': 'Secure Booking',
    'hero.secureDesc': 'Checkout and payment with the selected travel partner',
    'hero.poweredByAi': 'Powered by AI',
    'hero.planTripCta': 'Plan my trip with AI',
    'hero.findDealsCta': 'Find Best Deals',
    'hero.orBrowseDeals': 'or browse today\'s deals',
    'hero.trustedPartners': 'Explore options with',
    // Sprint A2 — editorial chip labels (amber)
    'chip.travelaiPick':     'TravelAI Pick',
    'chip.aiRecommendation': 'AI Recommendation',
    'chip.priceDrop':        'Price Drop',
    'chip.editorChoice':     'Editor\'s Choice',
    // Sprint B — TrustChip labels (real data only)
    'trust.cheapestToday':   'Cheapest today',
    'trust.nonstop':         'Non-stop',
    'trust.shortTransfer':   'Short transfer',
    'trust.fastest':         'Fastest',
    'trust.freeCancel':      'Free cancellation',
    'trust.updatedToday':    'Updated today',
    'trust.iata':            'IATA verified',
    'trust.familyFriendly':  'Family friendly',
    'trust.beachfront':      'Beachfront',
    'trust.aiPick':          'AI Pick',
    'trust.bestValue':       'Best value',
    'trust.mostBooked':      'Most booked',
    'trust.verified':        'Verified',
    // V4 · Intro Gate (new content — original copy unchanged elsewhere)
    'intro.headline': 'A quiet corner for',
    'intro.headlineAccent': 'extraordinary travel',
    'intro.subtitle': 'Your private secretary for the journeys others merely dream about. Flights, hotels, and rare finds — curated in silence.',
    'intro.enter': 'Enter',
    'intro.footnote': 'Est. Sofia · Curated for the discerning traveller',
    // V4 · Jarvis Rare Finds (new section)
    'rareFinds.kicker': 'Jarvis Picks',
    'rareFinds.title': 'Rare Finds',
    'rareFinds.subtitle': 'Three quiet discoveries your secretary set aside — for the traveller who has already seen the postcards.',
    'rareFinds.viewAll': 'View all rare finds',
    'rareFinds.footnote': 'Curated personally. Never sponsored.',
    // V5 · Smart Discovery Homepage
    'discovery.kicker':    'Live Discovery',
    'discovery.title':     'Real deals, refreshed daily.',
    'discovery.subtitle':  'Aviasales-observed prices for round-trips from Bulgaria. One click to book flights and matching hotels.',
    'discovery.deals':     'deals',
    'discovery.footnote':  'Prices observed by Aviasales · refreshed daily · confirm live availability at booking.',
    // V5 · Floating AI concierge widget
    'chat.floatingAria':   'Open AI concierge',
    'chat.floatingKicker': 'Live Concierge',
    'chat.floatingTitle':  'Chat with Jarvis',
    'rareFinds.cat.all':       'All',
    'rareFinds.cat.cities':    'Rare Cities',
    'rareFinds.cat.retreats':  'Boutique Retreats',
    'rareFinds.cat.cinematic': 'Cinematic Escapes',
    'rareFinds.partner.aviasales': 'See fares on Aviasales',
    'rareFinds.partner.booking':   'Book on Booking.com',
    // Editorial city cards (region + note per key)
    'rareFinds.valletta.region':  'Malta',
    'rareFinds.valletta.note':    'Baroque palazzo suites on Republic Street — arrive before the ferry crowd.',
    'rareFinds.tbilisi.region':   'Georgia',
    'rareFinds.tbilisi.note':     "Ornate wooden balconies in Sololaki — the Caucasus' most underestimated capital.",
    'rareFinds.ljubljana.region': 'Slovenia',
    'rareFinds.ljubljana.note':   'A pedestrian old town on the river — Vienna in miniature, without the queues.',
    'rareFinds.faro.region':      'Algarve · Portugal',
    'rareFinds.faro.note':        'Cliffside cottages at Ponta da Piedade — the ones the guidebooks quietly forgot.',
    'rareFinds.salzburg.region':  'Austria',
    'rareFinds.salzburg.note':    'A single suite above the old jewellers on Getreidegasse — steps from Mozart, quiet by night.',
    'rareFinds.bilbao.region':    'Basque Country · Spain',
    'rareFinds.bilbao.note':      'A discreet 18-room hotel behind the Guggenheim — the pintxos are two blocks away.',
    'rareFinds.kyoto.region':     'Japan',
    'rareFinds.kyoto.note':       'A ryokan in Higashiyama in November — maple season without the coach tours.',
    'rareFinds.marrakech.region': 'Morocco',
    'rareFinds.marrakech.note':   'A riad in the Kasbah quarter — courtyard fountains, rooftop tea, no reception desk.',
    'rareFinds.reykjavik.region': 'Iceland',
    'rareFinds.reykjavik.note':   'A design hotel two blocks from Harpa — winter aurora, summer white nights.',
    // V4 · Flight → Hotel cross-sell (new component)
    'crossSell.hotel.title': 'Shall I also secure the accommodation?',
    'crossSell.hotel.body': 'These dates open up a small selection of curated stays at your destination.',
    'crossSell.hotel.cta': 'Curate my stay',
    // V4 · Sofia → World identity mark (new hero micro-copy)
    'hero.identityMark': 'Sofia → World',
    // Sprint E · Mobile bottom nav — nav.home/flights/hotels/saved already
    // defined above with the desktop-header wording; only 'nav.chat' is new.
    'nav.chat':    'Chat',
    'hero.titlePrefix': 'Your personal',
    'hero.titleAccent': 'AI travel',
    'hero.titleSuffix': 'assistant',
    'hero.eyebrow': 'AI-Powered · Multi-City Trips · Partner Searches',
    // Homepage section headers (V3.5 editorial pass)
    'home.section.aiKicker': 'AI Concierge',
    'home.section.aiTitle': 'Start with a conversation, not a search bar.',
    'home.section.aiSubtitle': 'Tell us the vibe, the budget or a rough date range — we do the comparison work and hand back a plan.',
    'home.section.gemKicker': 'Editor\'s Pick',
    'home.section.gemTitle': "Today's hidden gem",
    'home.section.gemSubtitle': 'Travel inspiration for a less-obvious destination. Check seasonal conditions and entry requirements before you go.',
    'home.section.dealsKicker': 'Live Marketplace',
    'home.section.dealsTitle': 'Real prices from real partners.',
    'home.section.dealsSubtitle': 'Explore flight and hotel partner searches. Verify the final fare, availability and conditions before booking.',
    'hero.stat.destinations': 'Trip planner',
    'hero.stat.airlines': 'Languages',
    'hero.stat.partners': 'Itineraries',
    'hero.stat.fees': 'TravelAIBG planning fee',
    'hero.proof.routes': 'Organize a trip · check provider prices',
    'hero.proof.aiCopilot': 'AI concierge · plans in seconds',
    'hero.proof.bilingual': 'Travel planning in English and Bulgarian',
    'hero.proof.secure': 'Booking and payment on provider websites',
    'home.partnerLogosCaption': 'Explore flights and hotels with',
    // Phase-2 — HowItWorks section
    'home.howItWorksKicker': 'Why TravelAI',
    'home.howItWorksTitle': 'A better way to plan every trip',
    'home.howItWorksSub': 'One clear travel plan, with final offers checked at the provider.',
    'home.howItWorks.ask.title': 'Ask like a human',
    'home.howItWorks.ask.desc': 'Say what you want in plain English or Bulgarian — dates, budget, vibe. No filters to fight.',
    'home.howItWorks.compare.title': 'AI compares in seconds',
    'home.howItWorks.compare.desc': 'Our AI helps organize destinations, dates and travel preferences, then directs you to partner searches for final availability and prices.',
    'home.howItWorks.book.title': 'Book with the partner you trust',
    'home.howItWorks.book.desc': 'TravelAIBG helps you continue to a flight or hotel provider, where you verify terms and complete any booking.',
    // Phase-2 — HomepageFAQ section
    'home.faqKicker': 'Common questions',
    'home.faqTitle': 'Everything you might ask before booking',
    'home.faq.pricing.q': 'Are the prices I see real and up-to-date?',
    'home.faq.pricing.a': 'Prices displayed in TravelAIBG may be cached or indicative. Availability and final payable prices are confirmed on the airline, hotel or booking partner website before payment.',
    'home.faq.safety.q': 'Is it safe to book through TravelAI?',
    'home.faq.safety.a': 'TravelAIBG does not take payment for the flight or hotel. You leave our site to review the final price, availability, payment security, cancellations and confirmation directly with the provider.',
    'home.faq.fees.q': 'Do you charge any fees or commissions from me?',
    'home.faq.fees.a': 'TravelAIBG does not currently charge a separate planning fee. The provider controls the final total, including any taxes or fees. Affiliate earnings depend on the specific partner program and are not guaranteed by clicking a link.',
    'home.faq.aiTrust.q': 'Can I trust the AI recommendations?',
    'home.faq.aiTrust.a': 'AI suggestions help you explore destinations and build an itinerary. Some suggestions or prices may be indicative. Compare every hotel, flight and final rate with the supplier before paying.',
    'home.faq.cancellation.q': 'What about cancellations and changes?',
    'home.faq.cancellation.a': 'Cancellation, changes, luggage, taxes and payment rules depend on the selected provider and fare. Check the actual conditions on the supplier website before booking.',
    'home.faq.multiLang.q': 'Is TravelAI in Bulgarian too?',
    'home.faq.multiLang.a': 'Yes. The interface supports Bulgarian and English. Provider websites, booking confirmations and support may use different languages.',
    'chat.title': 'AI Travel Concierge',
    'chat.subtitle': 'AI travel planning · verify final prices',
    'chat.welcome': 'Hello! Tell me your destination, dates, preferences or budget. I can help build an itinerary and find partner searches, but prices and availability must be confirmed with the provider.',
    // Change 3 · Page-contextual welcome messages
    'chat.welcome.home':        'Hi! I\'m your personal TravelAI assistant. I can help you find cheap flights, hotels, or plan a full trip. Where do you dream of going?',
    'chat.welcome.flights':     'Looking for a flight? Tell me where you\'re flying from, where to, and when — I\'ll find the best prices.',
    'chat.welcome.hotels':      'Looking for a hotel? Tell me the destination, dates and number of guests — I\'ll surface the available options.',
    'chat.welcome.packages':    'Want to plan the whole trip? Tell me destination, dates and budget — I\'ll line up the flight, hotel and transfer.',
    'chat.welcome.destination': 'Planning a trip? I can help you find a flight and a hotel and line everything up in one place.',
    'chat.placeholder': 'Describe your ideal trip... (e.g., "7 days in Italy for 2 people, budget €1500")',
    'chat.liveIndicator': 'Live',
    'chat.listening': 'listening…',
    'chat.saveTrip': 'Save Trip',
    'chat.shareTrip': 'Share trip',
    'chat.saving': 'Saving…',
    'auth.signIn': 'Sign In',
    'auth.signUp': 'Sign Up',
    'auth.email': 'Email Address',
    'auth.password': 'Password',
    'auth.welcomeBack': 'Welcome back',
    'auth.createAccount': 'Create your account',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.signingIn': 'Signing in...',
    'auth.signingUp': 'Creating account...',
    'auth.fullName': 'Full Name',
    'nav.home': 'Home',
    'nav.flights': 'Flights',
    'nav.hotels': 'Hotels',
    'nav.packages': 'Packages',
    'nav.saved': 'Saved',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.search': 'Search',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.from': 'From',
    'common.to': 'To',
    'common.total': 'Total',
    'common.night': 'night',
    'common.nights': 'nights',
    'common.back': 'Back',
    'common.guest': 'guest',
    'common.guests': 'guests',
    'common.room': 'room',
    'common.rooms': 'rooms',
    'common.checkin': 'Check-in',
    'common.checkout': 'Check-out',
    'common.departure': 'Departure',
    'common.return': 'Return',
    'common.adults': 'Adults',
    'common.children': 'Children',
    'common.direct': 'Direct',
    'common.stop': 'stop',
    'common.stops': 'stops',
    'common.viewDeal': 'View deal',
    'common.explore': 'Explore',

    // Best Offers / Home
    'home.todaysTopDeals': "Today's Top Travel Deals",    'home.todaysTopDealsSub': 'Live flight prices and curated packages — handpicked for you',
    'home.todaysTopDealsKicker': 'Updated minutes ago',
    'home.searchFlights': 'Search Flights',
    'home.searchHotels': 'Search Hotels',
    'home.todaysBestFlightDeals': "Today's Best Flight Deals",
    'home.featuredPackages': 'Featured Packages',
    'home.livePrices': 'Observed partner prices · verify final offers',
    'home.partnerNote': 'Live deals updated regularly · Prices and availability sourced from partner platforms (Aviasales, Booking.com) · May vary at booking time',
    'home.noDeals': 'No live deals available right now. Try our flight search.',
    'home.flightDate': 'Flight date',
    'home.secureBooking': 'Secure Booking',
    'home.poweredBy': 'Powered by Global Travel Providers',
    'home.bestPrice': 'Compare options with our partners',
    'home.trustedFooter': 'Plan with AI · Review flight and hotel searches · Book independently with the provider',
    'home.popular': 'POPULAR',
    'home.new': 'NEW',
    'home.exclusive': 'EXCLUSIVE',
    'home.hot': 'TOP DEAL',
    'home.bestSeller': 'Best Seller',
    'home.limitedTime': 'Limited Time',
    'home.premiumPick': 'Premium Pick',

    // Flight search
    'flights.title': 'Find Your Perfect Flight',
    'flights.subtitle': 'Compare flights from our trusted global airlines network',
    'flights.findDeals': 'Find Best Deals',
    'flights.prefillTitle': 'Pre-filled from your AI chat:',
    'discover.tabs.all': 'All',
    'discover.flights.tabsLabel': 'Filter by flight category',
    'discover.hotels.tabsLabel': 'Filter by hotel category',
    'destination.missingKey': 'No destination selected.',
    'flights.fillFields': 'Please fill in all required fields',
    'flights.sameAirports': 'Origin and destination cannot be the same',
    'flights.errorSearching': 'Error searching flights',
    'flights.found': 'Flights Found',
    'flights.empty': 'Enter your route and dates to search for flights',
    'flights.reserve': 'Reserve',
    'flights.from': 'From',
    'flights.to': 'To',
    'flights.typePlaceholder': 'Type city or airport...',
    'flights.departure': 'Departure',
    'flights.return': 'Return',
    'flights.passengersClass': 'Passengers & Class',
    'flights.passengers': 'Passengers',
    'flights.adults': 'Adults',
    'flights.class': 'Class',
    'flights.travelClass': 'Travel Class',
    'flights.economy': 'Economy',
    'flights.premiumEconomy': 'Premium Economy',
    'flights.business': 'Business',
    'flights.first': 'First',
    'flights.firstClass': 'First Class',
    // Sprint D · Search Experience Overhaul
    'flights.searchingTitle': 'Finding the best flight deals…',
    'flights.searchingSubtitle': 'Comparing 700+ airlines in real time',
    'flights.countOne': 'flight',
    'flights.countMany': 'flights',
    'flights.filtersTitle': 'Refine your flights',
    'flights.sort.best': 'Best',
    'flights.sort.cheapest': 'Cheapest',
    'flights.sort.fastest': 'Fastest',
    'flights.sort.direct': 'Direct first',
    'flights.filter.stopsTitle': 'Stops',
    'flights.filter.anyStops': 'Any stops',
    'flights.filter.directOnly': 'Direct only',
    'flights.filter.maxOneStop': 'Max 1 stop',
    'flights.filter.priceTitle': 'Price range',
    'flights.filter.airlinesTitle': 'Airlines',
    'flights.filteredOut.title': 'No flights match these filters',
    'flights.filteredOut.body': 'Try loosening a filter to see more options.',
    'flights.filteredOut.cta': 'Reset filters',
    'flights.empty.title': "We couldn't find any flights",
    'flights.empty.body': 'Try nearby airports, wider dates, or different destinations.',
    'flights.empty.cta': 'Edit search',

    // Hotel search
    'hotels.title': 'Find Your Perfect Hotel',
    'hotels.subtitle': 'Compare hotels from our trusted global partners',
    'hotels.search': 'Search Hotels',
    'hotels.findDeals': 'Find Best Deals',
    'hotels.prefillTitle': 'Pre-filled from your AI chat:',
    'hotels.fillFields': 'Please fill in all required fields',
    'hotels.errorSearching': 'Error searching hotels',
    'hotels.found': 'Hotels Found',
    'hotels.empty': 'Enter your destination and dates to search for hotels',
    'hotels.reserve': 'Reserve',
    'hotels.destination': 'Destination',
    'hotels.typeCity': 'Type city name...',
    'hotels.noResults': 'No destinations found. Try: Barcelona, Paris, Rome...',
    'hotels.checkIn': 'Check-in',
    'hotels.checkOut': 'Check-out',
    'hotels.guestsRooms': 'Guests & Rooms',
    'hotels.adults': 'Adults',
    'hotels.children': 'Children',
    'hotels.rooms': 'Rooms',
    'hotels.guests': 'guests',
    'hotels.room': 'room',
    'hotels.child': 'Child',
    'hotels.childrenBirthHint': 'Birth date (optional — for accurate infant/child fare):',
    'hotels.foundCount': 'Hotels Found',
    // Sprint D · Search Experience Overhaul
    'hotels.searchingTitle': 'Comparing thousands of properties…',
    'hotels.searchingSubtitle': 'Live availability from Booking.com and partners',
    'hotels.countOne': 'stay',
    'hotels.countMany': 'stays',
    'hotels.filtersTitle': 'Refine your stays',
    'hotels.sort.recommended': 'Recommended',
    'hotels.sort.cheapest': 'Cheapest',
    'hotels.sort.topRated': 'Top rated',
    'hotels.sort.luxury': 'Luxury first',
    'hotels.filter.ratingTitle': 'Guest rating',
    'hotels.filter.ratingAny': 'Any rating',
    'hotels.filter.priceTitle': 'Price / night',
    'hotels.filter.typeTitle': 'Property type',
    'hotels.filter.amenitiesTitle': 'Amenities & policies',
    'hotels.filter.freeCancel': 'Free cancellation',
    'hotels.filteredOut.title': 'No stays match these filters',
    'hotels.filteredOut.body': 'Try loosening a filter to see more options.',
    'hotels.filteredOut.cta': 'Reset filters',
    'hotels.empty.title': "We couldn't find any stays",
    'hotels.empty.body': 'Try nearby cities, longer stays, or fewer guests.',
    'hotels.empty.cta': 'Edit search',

    // Dashboard / Profile
    'dashboard.welcomeBack': 'Welcome back!',
    'dashboard.signInRequired': 'Sign in to view your dashboard',
    'dashboard.signInDesc': 'Access your saved trips, bookings, and chat history',
    'dashboard.savedTrips': 'Saved Trips',
    'dashboard.bookings': 'Bookings',
    'dashboard.chatSessions': 'Chat Sessions',
    'dashboard.tab.overview': 'Overview',
    'dashboard.tab.trips': 'Trips',
    'dashboard.tab.bookings': 'Bookings',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.noActivity': 'No recent activity. Start chatting with the AI assistant!',
    'dashboard.noTrips': 'No saved trips yet. Start planning and save your favorite trips!',
    'dashboard.yourBookings': 'Your Bookings',
    'dashboard.noBookings': 'No bookings yet. Find your perfect trip and book today!',
    'dashboard.deleteConfirm': 'Are you sure you want to delete this trip?',
    'dashboard.tripDeleted': 'Trip deleted successfully',
    'dashboard.deleteFailed': 'Failed to delete trip',
    'dashboard.unknownDest': 'Unknown destination',
    'dashboard.savedAtPrefix': 'Saved',
    'dashboard.bookingIdLabel': 'Booking ID',
    'dashboard.hotelsCount': 'hotels',
    'dashboard.flightsCount': 'flights',

    // Trip Comparison
    'compare.title': 'Compare Trips',
    'compare.subtitle': 'Select up to 3 trips to compare side by side',
    'compare.empty.title': 'No saved trips yet',
    'compare.empty.body': 'Start chatting with the AI assistant to plan trips, then save them for comparison.',
    'compare.selectAtLeast2': 'Select at least 2 trips to compare them side by side',
    'compare.maxThree': 'You can compare up to 3 trips at a time',
    'compare.signInToCompare': 'Sign in to save and compare trips',
    'compare.heading': 'Trip Comparison',
    'compare.notSpecified': 'Not specified',
    'compare.noHotels': 'No hotels',
    'compare.noFlights': 'No flights',
    'compare.col.feature': 'Feature',
    'compare.col.duration': 'Duration',
    'compare.col.budgetType': 'Budget Type',
    'compare.col.hotels': 'Hotels',
    'compare.col.flights': 'Flights',
    'compare.budget.budget': 'Budget',
    'compare.budget.standard': 'Standard',
    'compare.budget.luxury': 'Luxury',

    // Booking flow
    'booking.tripSummary': 'Your Trip Summary',
    'booking.reviewBeforeConfirm': 'Review before continuing to the travel partner',
    'booking.completeReservation': 'Complete Reservation',
    'booking.secureGuarantee': 'Partner checkout',
    'booking.freeCancellation': 'Free cancellation',
    'booking.secureBookingItem': 'Secure booking',
    'booking.bestPriceGuarantee': 'Final price confirmed by provider',
    'booking.verifiedSecure': 'Review provider prices and terms before payment',
    'booking.securingDeal': 'Opening the travel partner...',
    'booking.holdOn': 'Please hold on while we prepare your reservation',
    'booking.partnerNote': 'Booking is completed via trusted partners (Aviasales, Booking.com). Prices and availability are sourced live from these platforms.',
    'booking.estimateNote': 'Estimated total · final price set by partner platform',
    // Destination detail modal (used by /packages, popular destinations, AI chat)
    'destination.loading': 'Loading destination...',
    'destination.unavailable.title': 'No live data for this destination yet',
    'destination.unavailable.body': 'We only show packages when we can back them with real flight + hotel data. Browse our supported destinations on the Flights or Hotels page.',
    'destination.unavailable.cta': 'Browse destinations',
    'destination.flights': 'Flights',
    'destination.hotels': 'Stays',
    'destination.insiderTips': 'Insider tips',
    'destination.viewFlights': 'View flights',
    'destination.compareDistributors': 'Compare prices across our partners — pick the lowest:',
    'destination.reserveOnPartner': 'Reserve on {partner}',
    'destination.tier.cheap': 'Cheap',
    'destination.tier.balanced': 'Balanced',
    'destination.tier.premium': 'Premium',
    'destination.tier.budget': 'Budget',
    'destination.tier.standard': 'Mid-range',
    'destination.tier.luxury': 'Luxury',
    // Discovery / Inspiration grid (Flights & Hotels pages)
    'discover.priceLabel': 'From',
    'discover.cta': 'Explore',
    'discover.badge.hot': 'Hot',
    'discover.badge.bestValue': 'Best Value',
    'discover.badge.popular': 'Popular',
    'discover.badge.luxury': 'Luxury',
    'discover.badge.premium': 'Premium',
    'discover.badge.quick': 'Quick Trip',
    'discover.badge.romantic': 'Romantic',
    'discover.badge.family': 'Family',
    'discover.badge.smartDeal': 'Smart Deal',
    'discover.flights.smartDeals.title': 'Smart Deals',
    'discover.flights.smartDeals.subtitle': 'Routes with unusually favourable pricing right now',
    'discover.flights.intro.title': 'Where to next?',
    'discover.flights.intro.subtitle': 'Hand-picked routes our travelers love right now',
    'discover.flights.trending.title': 'Trending destinations',
    'discover.flights.trending.subtitle': 'The most searched routes this week',
    'discover.flights.cheap.title': 'Cheap flight deals',
    'discover.flights.cheap.subtitle': 'Lowest fares spotted from your origin',
    'discover.flights.weekend.title': 'Weekend getaways',
    'discover.flights.weekend.subtitle': 'Short hops · perfect for 2-3 nights',
    'discover.flights.luxury.title': 'Premium escapes',
    'discover.flights.luxury.subtitle': 'Long-haul, business class & sun all year',
    'discover.hotels.intro.title': 'Stays you will love',
    'discover.hotels.intro.subtitle': 'Curated cities, hand-picked tiers, instant compare',
    'discover.hotels.budget.title': 'Budget recommendations',
    'discover.hotels.budget.subtitle': 'Great value cities · 3★ stays from €40/night',
    'discover.hotels.midRange.title': 'Mid-range stays',
    'discover.hotels.midRange.subtitle': 'Stylish 4★ properties for the smart traveler',
    'discover.hotels.luxury.title': 'Luxury stays',
    'discover.hotels.luxury.subtitle': '5★ resorts, suites and beachfront escapes',
    'discover.hotels.romantic.title': 'Romantic escapes',
    'discover.hotels.romantic.subtitle': 'Two-for-the-road cities for couples',
    'discover.hotels.family.title': 'Family destinations',
    'discover.hotels.family.subtitle': 'Kid-friendly cities, beaches & culture',
    'discover.trust.title': 'Click any card · we will compare partners for you',
    'discover.trust.subtitle': 'Aviasales · Booking.com · Hotels.com · Agoda · Trivago — we pull live pricing so you book with confidence.',

    'booking.verifiedPartner': 'Verified partner',
    'booking.instantConfirm': 'Instant confirmation',
    'booking.tapToContinue': 'Tap to continue to booking',
    // iter80 — premium RedirectOverlay copy
    'redirect.heading': 'Booking with an official partner',
    'redirect.opening': 'Opening your secure checkout…',
    'redirect.route': 'Route',
    'redirect.destination': 'Destination',
    'redirect.hotel': 'Hotel',
    'redirect.airline': 'Airline',
    'redirect.item': 'Selection',
    'redirect.dates': 'Dates',
    'redirect.stay': 'Stay',
    'redirect.travellers': 'Travellers',
    'redirect.displayedPrice': 'Displayed price',
    'redirect.priceCaveat': 'Final price is confirmed on the partner site before you pay.',
    'redirect.trust.officialPartner': 'External provider — review terms',
    'redirect.trust.securePayment': 'Secure payment on the partner site',
    'redirect.trust.noHiddenFees': 'No hidden fees or markups',
    // Sprint B — save-this-deal recovery on RedirectOverlay
    'redirect.saveThisDeal': 'Save this deal',
    'redirect.saved':        'Saved',
    'redirect.priceAlert':   'Set price alert',

    // Chat extras
    'chat.send': 'Send',
    'chat.thinking': 'Thinking...',
    'chat.suggestionsTitle': 'Try asking:',
    'chat.book': 'Book',
    'chat.error': 'Sorry, I had trouble responding. Please try again in a moment.',
    'chat.streamAnalyzing': 'Analyzing your request…',
    'chat.streamBuildingPlan': 'Building your itinerary…',
    'chat.streamFinalizing': 'Finalizing details…',
    'chat.priceDisclaimer': 'Prices via partner platforms · may vary',
    'chat.chip.cheapest': 'Cheapest',
    'chat.chip.fastest': 'Fastest',
    'chat.chip.direct': 'Direct',
    'chat.chip.fewestStops': 'Fewest stops',
    'chat.chip.topRated': 'Highest rating',
    'chat.chip.bestValue': 'Best value',
    'chat.chip.centrallyLocated': 'City centre',
    'chat.chip.freeCancellation': 'Free cancellation',
    'chat.chip.breakfast': 'Breakfast',
    // Phase-3 · CardMetaLine — one line per card answering why + who + what
    'chat.meta.why.topRated':          'Top-rated pick',
    'chat.meta.why.bestValue':         'Best overall value',
    'chat.meta.why.centrallyLocated':  'Central location',
    'chat.meta.why.freeCancellation':  'Flexible cancellation',
    'chat.meta.why.breakfast':         'Breakfast included',
    'chat.meta.why.cheapest':          'Lowest price today',
    'chat.meta.why.fastest':           'Shortest journey',
    'chat.meta.why.direct':            'Direct — no stops',
    'chat.meta.why.fewestStops':       'Fewest layovers',
    'chat.meta.for.families':          'Great for families',
    'chat.meta.for.couples':           'Great for couples',
    'chat.meta.for.business':          'Great for business',
    'chat.meta.for.solo':              'Great for solo travellers',
    'chat.meta.for.special':           'Great for a special stay',
    'chat.meta.for.timePressed':       'Great if you value time',
    'chat.meta.for.budget':            'Great on a budget',
    'chat.meta.what.hotel':            'Continue on {partner} — final price shown at checkout',
    'chat.meta.what.hotelFallback':    'Booking completes on our verified hotel partner',
    'chat.meta.what.flightFallback':   'Continue on Aviasales — price locked before payment',
    'chat.meta.what.transport':        'Continue on {partner} — verified transfer partner',
    'chat.meta.what.transportFallback':'Booking completes on our verified transport partner',
    'chat.style.budget': 'Budget',
    'chat.style.romantic': 'Romantic',
    'chat.style.adventure': 'Adventure',
    'chat.style.family': 'Family',
    'chat.style.nightlife': 'Nightlife',
    'chat.style.luxury': 'Luxury',
    'chat.transports': 'Ground transport',
    'chat.transportCta': 'Check options',
    'chat.alternativeLabel': 'Smart alternative',
    'chat.flightOptions': 'Flight Options',
    'chat.hotelOptions': 'Hotel Options',
    'chat.savedTrip': 'Trip saved!',
    'chat.signinToSave': 'Please sign in to save trips',
    'chat.errorSending': 'Failed to send message. Please try again.',
    'chat.starterTitle': 'Pick a vibe to get started',
    'chat.starter1': 'Surprise me this weekend',
    'chat.starter2': 'Family trip with kids under €2000',
    'chat.starter3': 'Romantic escape for two',
    'chat.starter4': 'Beach holiday on a budget',
    'chat.starter5': 'Luxury anniversary getaway',
    'chat.starter6': 'Hidden gem under €1000',
    'chat.starterShowMore': 'Show more ideas',
    'chat.alternativesTitle': 'Other options to consider',
    'chat.alternativesPlan': 'Plan this',
    'chat.starterShowLess': 'Show fewer',
    'chat.insiderTips': 'Insider tips from your concierge',
    'chat.bestTime': 'Best time',
    'chat.localGem': 'Local gem',
    'chat.thinkingFlights': 'Scanning live flights from your airport…',
    'chat.thinkingHotels': 'Comparing central hotels…',
    'chat.thinkingDeals': 'Hunting the best real-time deals…',
    'chat.thinkingCrafting': 'Crafting your premium itinerary…',
    // Quick-action chips & trip summary & message actions
    'chat.quickCheaper': 'Make it cheaper',
    'chat.quickLuxe': 'Show me luxury options',
    'chat.quickDates': 'Different dates',
    'chat.quickAddCity': 'Add another city',
    'chat.quickShorter': 'Shorter trip',
    'chat.quickFamily': 'Family-friendly',
    'chat.quickDifferentHotels': 'Different hotels',
    'chat.quickDifferentFlights': 'Different flights',
    'chat.quickDifferentCity': 'Try another city',
    'chat.refineTitle': 'Refine this plan:',
    'chat.summaryTitle': 'Your trip in progress',
    'chat.summaryTotal': 'Estimated total',
    'chat.summaryTravelers': 'Travelers',
    'chat.summaryClear': 'Start over',
    'chat.jumpToBookings': 'Ready to book',
    'chat.copy': 'Copy',
    'chat.copied': 'Copied!',

    // Package detail modal
    'package.duration': 'Duration',
    'package.rating': 'Rating',
    'package.fromPrice': 'From',
    'package.highlights': "What's included",
    'package.suggestedFlight': 'Suggested Flight',
    'package.suggestedHotel': 'Suggested Hotel',
    'package.perNight': 'per night',
    'package.bookEntire': 'Book Entire Package',
    'package.bookEntireSub': 'Opens flight & hotel reservations in new tabs',
    'package.optionalExtensions': 'Optional extensions',
    'package.optionalNote': 'Optional add-on — book separately',
    'package.exploreCity': 'Explore',
    'package.partnerNote': 'Booking is completed via trusted partners (Aviasales, Booking.com). Prices may vary depending on availability.',
    'package.estimatedTotal': 'Estimated total',
    'package.timeline': 'Trip timeline',
    'package.returnLeg': 'Return flight',
    'package.priceDisclaimer': 'Estimated range. Prices may vary depending on availability.',
    'package.startSequentialBooking': 'Start sequential booking',
    'package.customizeWithAI': 'Customize itinerary with AI',
    'package.aiCustomizePrompt': 'I want to customize the {name} package ({nights} nights). Help me adjust the cities, dates, and stay durations to fit my preferences.',
    'package.aiCustomizeIntro': "I've got your {name} ready to personalise! Let me ask you 3 quick questions to make it perfect for you:",
    'package.aiCustomizeQ1': '1. 🏨 Hotels: Do you prefer budget (€40-70/night), comfort (€80-150), or something special (€150+)?',
    'package.aiCustomizeQ2': '2. 📅 Dates: The suggested dates are {dates}. Do these work, or shall we adjust?',
    'package.aiCustomizeQ3': '3. 🔄 Destinations: Your package includes {cities}, with optional stops in {extras}. Any you\'d like to swap or add?',
    'package.aiCustomizeClose': "Once confirmed, I'll show you the full package with live prices, flights, hotels and booking links — all in one place.",
    'package.aiCustomizeDatesFlex': 'flexible',
    'package.stepper.stepLabel': 'Step {current} of {total}',
    'package.stepper.flightCta': 'Find this flight',
    'package.stepper.partnerNote': 'Each booking opens in a new tab. Tap Next segment when you\'re done.',
    'package.stepper.next': 'Next segment',
    'package.stepper.prev': 'Previous',
    'package.stepper.finish': 'Finish trip plan',
    'package.stepper.empty': 'No bookable segments are available for this package right now. Please try again later.',
    'trip.shareText': 'Check out this trip I\'m planning on TravelAI',
    'trip.copyPrompt': 'Copy this link to share:',
    'trip.sharedHeading': 'Shared trip plan',
    'trip.flight': 'Flight',
    'trip.hotel': 'Stay',
    'trip.cta.title': 'Like what you see?',
    'trip.cta.body': 'Plan this trip yourself in 60 seconds — same itinerary, your dates.',
    'trip.cta.plan': 'Plan this trip yourself',
    'trip.cta.share': 'Share trip',
    'trip.cta.copied': 'Link copied!',
    'trip.footer': 'Powered by TravelAI · All bookings completed via trusted partners (Aviasales, Booking.com).',
    'trip.notFound.title': 'This trip plan isn\'t available',
    'trip.notFound.body': 'The link may be outdated. Browse our curated travel packages to start planning your next adventure.',
    'trip.notFound.browse': 'Browse packages',
    'search.flightPlaceholder': 'Search any city worldwide…',
    'search.hotelPlaceholder': 'Where would you like to stay?',
    'search.flightCta': 'Search flights',
    'search.hotelCta': 'Search stays',
    'search.tap': 'Tap to select',
    'alerts.title': 'Get notified when {city} drops',
    'alerts.subtitle': 'We\'ll email you the moment a fare drops below your target. No spam.',
    'alerts.emailLabel': 'Email',
    'alerts.thresholdLabel': 'Notify me below (€) for {city}',
    'alerts.submitCta': 'Set price alert',
    'alerts.privacyNote': 'We use your email only for this alert. Unsubscribe any time.',
    'alerts.successTitle': 'You\'re all set!',
    'alerts.successBody': 'We\'ll email you when the price drops below your target.',
    'alerts.invalidEmail': 'Please enter a valid email.',
    'alerts.error': 'Something went wrong. Please try again.',

    // Footer
    'footer.brandTagline': 'Revolutionising travel planning with AI. Discover incredible destinations, find the best deals, and create unforgettable memories — from Sofia to anywhere.',
    'footer.quickLinks': 'Quick Links',
    'footer.partners': 'Our Partners',
    'footer.support': 'Support',
    'footer.helpCenter': 'Help Center',
    'footer.contactUs': 'Contact Us',
    'footer.faq': 'FAQ',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.contact': 'Contact Us',
    'footer.location': 'Plovdiv, Bulgaria',
    'footer.copyright': 'All rights reserved.',
    'footer.privacyShort': 'Privacy',
    'footer.termsShort': 'Terms',
    'footer.cookies': 'Cookies',
    // P0 pre-launch — real trust signals only
    'footer.trust.iata': 'Compare flights and hotels on provider sites',
    'footer.trust.ssl': 'Booking and payment take place at the provider',
    'footer.trust.gdpr': 'TravelAIBG does not collect card payments',
    'footer.localeMarker': 'Travel offers shown in EUR · airports vary',
    // Legal placeholder — updated with UIC / VAT once the entity is registered
    'footer.legalEntity': 'TravelAIBG · Bulgaria',

    // Auth gate (soft wall before booking)
    'authGate.title': 'Save your travel plans',
    'authGate.subtitle': 'Sign in to save and revisit your itineraries. You can open supplier searches without an account.',
    'authGate.titleSignup': 'Create your free account',
    'authGate.subtitleSignup': 'Keep your travel plans and preferences together. Booking and payment remain with the provider.',
    'authGate.titleSignin': 'Welcome back',
    'authGate.subtitleSignin': 'Sign in to access your saved travel plans.',
    'authGate.benefitSave': 'Save trips, hotels & flights instantly',
    'authGate.benefitAlerts': 'Get personal price-drop alerts',
    'authGate.benefitSecure': 'Secure, no spam — one Google tap',
    'authGate.continueWithGoogle': 'Continue with Google',
    'authGate.googleReassurance': "You'll be redirected to Google's secure sign-in. We never see or store your password.",
    'authGate.googleBenefit1': 'One tap — no new password to remember',
    'authGate.googleBenefit2': 'We only receive your name, email and profile photo',
    'authGate.googleBenefit3': 'You can revoke access anytime from your Google Account',
    'authGate.oauthBadge': 'Standard OAuth 2.0 · Trusted by millions',
    'authGate.continueWithFacebook': 'Continue with Facebook',
    'authGate.or': 'or use email instead',
    'authGate.name': 'Name',
    'authGate.namePlaceholder': 'Your name',
    'authGate.email': 'Email',
    'authGate.emailPlaceholder': 'you@example.com',
    'authGate.password': 'Password',
    'authGate.createAccount': 'Create account',
    'authGate.signIn': 'Sign in',
    // P0 trust row — verifiable capabilities only
    'authGate.trust.ssl': 'SSL secure',
    'authGate.trust.gdpr': 'Privacy information available',
    'authGate.trust.noCard': 'No card required',
    'authGate.haveAccount': 'Already have an account?',
    'authGate.noAccount': "Don't have an account?",
    'authGate.fillFields': 'Please fill in all fields.',
    'authGate.passwordTooShort': 'Password must be at least 6 characters.',
    'authGate.genericError': 'Something went wrong. Please try again.',
    'authGate.fbComingSoon': 'Facebook sign-in coming soon. Please use Google or email for now.',
    'authGate.terms': 'By continuing you agree to our Terms and Privacy Policy.',
    'authGate.forgotPassword': 'Forgot password?',
    'authGate.titleForgot': 'Reset your password',
    'authGate.subtitleForgot': "Enter your email and we'll send you a link to choose a new password.",
    'authGate.titleForgotSent': 'Check your inbox',
    'authGate.subtitleForgotSent': 'A reset link is on its way',
    'authGate.forgotSentBody': "We just emailed a password reset link. It expires in 60 minutes. If you don't see it, check your spam folder.",
    'authGate.sendResetLink': 'Send reset link',
    'authGate.backToSignIn': 'Back to sign in',
    'authGate.rememberMe': 'Keep me signed in',
    'authGate.rememberMeHint': 'Stay signed in on this device for 30 days',
    'authGate.pwdStrength.weak': 'Weak — add more characters',
    'authGate.pwdStrength.fair': 'Fair — add a number or symbol',
    'authGate.pwdStrength.good': 'Good password',
    'authGate.pwdStrength.strong': 'Strong password',
    'hotel.nearby': 'Nearby',
    'hotel.estimate': 'AI example · real prices on click',
    'hotel.estimateTooltip': 'This is an AI example. Real availability and pricing load when you click Book.',
    'chat.hotelBookNoCity': "I can't open a booking without a destination. Try a new search.",
    // Profile completion modal — required before booking redirects to partners
    'profileGate.title': 'Almost there — one quick detail',
    'profileGate.subtitle': 'Update your optional contact details. A telephone number is not required to open an external supplier search.',
    'profileGate.phoneLabel': 'Phone (with country code)',
    'profileGate.phonePlaceholder': '+359 88 123 4567',
    'profileGate.countryLabel': 'Country',
    'profileGate.countryPlaceholder': 'Bulgaria',
    'profileGate.continueBtn': 'Save account details',
    'profileGate.skipBtn': 'Cancel',
    'profileGate.error.phone': 'Please enter a valid phone number (4+ digits).',
    'profileGate.benefitFasterBooking': 'Keep your account details up to date',
    'profileGate.benefitAlerts': 'Get instant price-drop alerts',
    'profileGate.benefitTrust': 'We never share your number',
    // Admin inbox
    'admin.inbox.title': 'Contact Inbox',
    'admin.inbox.subtitle': 'Messages submitted via the contact form',
    'admin.inbox.unread': '{n} unread',
    'admin.inbox.empty': 'No messages yet. Check back later.',
    'admin.inbox.markAllRead': 'Mark all as read',
    'admin.inbox.refresh': 'Refresh',
    'admin.inbox.toggleUnread': 'Show unread only',
    'admin.inbox.from': 'From',
    'admin.inbox.subject': 'Subject',
    'admin.inbox.received': 'Received',
    'admin.inbox.delivered': 'Email delivered',
    'admin.inbox.pendingDelivery': 'Email pending (saved to DB)',
    'admin.inbox.replyCta': 'Reply via email',
    'admin.inbox.forbidden': "You don't have access to this page.",
    'admin.inbox.loading': 'Loading messages…',
    'reset.title': 'Choose a new password',
    'reset.subtitle': 'Pick something at least 6 characters long.',
    'reset.newPassword': 'New password',
    'reset.confirmPassword': 'Confirm new password',
    'reset.placeholder': 'New password',
    'reset.confirmPlaceholder': 'Re-enter your new password',
    'reset.submit': 'Update password',
    'reset.minLength': 'Password must be at least 6 characters',
    'reset.mismatch': "Passwords don't match",
    'reset.invalidToken': 'This reset link is missing or invalid. Please request a new one.',
    'reset.genericError': "Something went wrong. Please try again.",
    'reset.successTitle': 'Password updated',
    'reset.successBody': "You're signed in. Let's plan your next trip.",
    'reset.continueCta': 'Continue to TravelAI',

    // Auth callback (post-Google return page)
    'authCallback.signingIn': 'Signing you in...',
    'authCallback.almostThere': 'Almost there.',
    'authCallback.welcome': 'Welcome aboard!',
    'authCallback.taking': 'Taking you to TravelAI...',
    'authCallback.failed': 'Sign-in failed',
    'authCallback.backHome': 'Back to home',

    // Packages page
    'packages.headerBadge': 'Curated by our travel experts',
    'packages.title': 'Curated Travel Packages',
    'packages.subtitle': 'Hand-picked Sofia-departing experiences for every traveller — from budget weekends to once-in-a-lifetime escapes.',
    'packages.viewDetails': 'View details',
    'packages.popularDestinations': 'Popular destinations',
    // Per-package localised copy
    'packages.pkg-budget.name': 'Balkan Budget Escape',
    'packages.budgetDesc': '5-day getaway from Sofia to nearby gems',
    'packages.budgetLong': 'Discover the rich culture and stunning landscapes of the Balkans on this affordable 5-day adventure. Explore historic Athens, soak in the Aegean coastline of Thessaloniki, and immerse yourself in the vibrant culture of Belgrade — all from just €299.',
    'packages.budgetH1': 'Acropolis & Parthenon guided tour',
    'packages.budgetH2': 'Sunset cruise from Piraeus',
    'packages.budgetH3': 'Traditional taverna food experience',
    'packages.budgetH4': 'Day trip to Cape Sounion',
    'packages.budgetH5': 'Free time in vibrant Monastiraki',
    'packages.pkg-family.name': 'Mediterranean Family Trip',
    'packages.familyDesc': '7 days from Sofia — sun, sea & culture',
    'packages.familyLong': 'A 7-day Mediterranean adventure designed for families. Walk the historic streets of Barcelona, marvel at the Roman Colosseum, and soak up the Italian sunshine in Naples. Includes child-friendly hotels and easy connections.',
    'packages.familyH1': 'Sagrada Família & Park Güell tour',
    'packages.familyH2': 'Beach day at Barceloneta',
    'packages.familyH3': 'Gothic Quarter walking tour',
    'packages.familyH4': 'Family-friendly tapas evening',
    'packages.familyH5': 'Day trip to Montserrat',
    'packages.pkg-luxury.name': 'Luxury Mediterranean Escape',
    'packages.luxuryDesc': '10-day premium experience from Sofia',
    'packages.luxuryLong': 'A 10-day premium experience for travellers seeking the finest. Begin in Sofia, fly to the volcanic beauty of Santorini, party in Mykonos, and end with the futuristic luxury of Dubai. 5-star stays, business-class flights, private transfers throughout.',
    'packages.luxuryH1': 'Private caldera sunset cruise',
    'packages.luxuryH2': 'Wine tasting at Santo Wines',
    'packages.luxuryH3': 'Helicopter transfer to Mykonos',
    'packages.luxuryH4': 'Beachfront lunch at Nammos',
    'packages.luxuryH5': 'Burj Khalifa VIP observation tower',
    // Popular destinations strip
    'packages.dest.paris.name': 'Paris',
    'packages.dest.paris.country': 'France',
    'packages.dest.barcelona.name': 'Barcelona',
    'packages.dest.barcelona.country': 'Spain',
    'packages.dest.rome.name': 'Rome',
    'packages.dest.rome.country': 'Italy',
    'packages.dest.amsterdam.name': 'Amsterdam',
    'packages.dest.amsterdam.country': 'Netherlands',
    'packages.dest.mykonos.name': 'Mykonos',
    'packages.dest.mykonos.country': 'Greece',
    'packages.dest.dubai.name': 'Dubai',
    'packages.dest.dubai.country': 'UAE',
    'packages.dest.naples.name': 'Naples',
    'packages.dest.naples.country': 'Italy',
    'packages.dest.thessaloniki.name': 'Thessaloniki',
    'packages.dest.thessaloniki.country': 'Greece',
    'packages.dest.belgrade.name': 'Belgrade',
    'packages.dest.belgrade.country': 'Serbia',

    // Saved page
    'saved.title': 'Saved items',
    'saved.subtitle': 'Your saved trips, flights and hotels',
    'saved.emptyTitle': 'Sign in to view saved items',
    'saved.emptyDescription': 'Save trips, compare options, and pick up exactly where you left off — across any device.',
    'saved.signInCta': 'Sign in to continue',

    // Profile page
    'profile.title': 'My profile',
    'profile.subtitle': 'Manage your account and preferences',
    'profile.emptyTitle': 'Sign in to access your profile',
    'profile.emptyDescription': 'View bookings, manage preferences, and personalise your travel feed.',
    'profile.signInCta': 'Sign in',

    // ===== STATIC FOOTER PAGES =====
    'common.back': 'Back to home',
    'page.lastUpdated': 'Last updated',

    // Help Center
    'page.help.title': 'Help Center',
    'page.help.lead': 'Need a hand? You\'re in the right place. Below are the answers to the questions we get asked most. If anything is missing, just write to us from the Contact Us page and we\'ll get back to you within one business day.',
    'page.help.section.1.heading': 'How does TravelAI work?',
    'page.help.section.1.body': 'TravelAI is an AI-powered travel concierge. Tell our chat where you\'d like to go (or just describe the kind of trip you want — "weekend in a big city under 500€", "family beach holiday in July") and we instantly build a full itinerary with flights, hotels, daily plan and a price estimate. When you click Book, we redirect you to the partner site (Aviasales, Booking.com, Hotels.com, etc.) where the actual purchase happens — securely, on the partner\'s checkout.',
    'page.help.section.2.heading': 'Do you sell flights and hotels directly?',
    'page.help.section.2.body': 'No. We are an affiliate aggregator — we surface the best deals across our partners and earn a small commission when you book through us. That commission costs you nothing extra; the price you see on the partner page is exactly what you pay. Your booking, your money, and your customer support relationship are all directly with the partner (the airline, the hotel chain, or Booking.com / Aviasales).',
    'page.help.section.3.heading': 'Are the prices I see real?',
    'page.help.section.3.body': 'We label every price clearly with one of three tags:\n• "Live" (green dot) — a cached price from a partner API, accurate at fetch time.\n• "Est." or "~€X" — an estimated range based on historical fares for that route or hotel category.\n• "From €X" — a promotional entry-point floor for our featured packages.\nThe final confirmed price is always whatever the partner shows on their checkout page. We never charge you directly.',
    'page.help.section.4.heading': 'How do I save trips and get price alerts?',
    'page.help.section.4.body': 'Create a free account (one-tap Google sign-in or email/password). Once you\'re in, every trip you build in the AI chat can be saved to your dashboard. From any flight result you can also tap the bell icon to subscribe to price-drop alerts — we\'ll email you the moment a fare drops below your threshold.',
    'page.help.section.5.heading': 'My booking confirmation didn\'t arrive — what now?',
    'page.help.section.5.body': 'The confirmation comes from the partner you booked with (Booking.com, the airline, etc.), NOT from TravelAI. Check the inbox of the email you used at their checkout (and your spam folder). If you used an alias or typed the wrong address, contact the partner\'s customer service directly — their phone and email are usually in your bank-statement description.',
    'page.help.section.6.heading': 'Still stuck?',
    'page.help.section.6.body': 'Drop us a line at travelaibg@gmail.com or use the Contact Us form. We reply to every message — usually within a few hours during European business days.',

    // FAQ
    'page.faq.title': 'Frequently Asked Questions',
    'page.faq.lead': 'Short answers to the most common questions about TravelAI.',
    'page.faq.section.1.heading': 'Is TravelAI free to use?',
    'page.faq.section.1.body': 'Yes. Building trips, browsing flights and hotels, getting price-drop alerts — all completely free for you. We earn a small affiliate commission from our partners when you book, paid by the partner, not added to your price.',
    'page.faq.section.2.heading': 'Do I need an account?',
    'page.faq.section.2.body': 'You can browse and chat without one. You only need to create an account when you want to save a trip, subscribe to price alerts, or complete a booking — so we can email you the confirmation.',
    'page.faq.section.3.heading': 'Which currencies do you support?',
    'page.faq.section.3.body': 'Prices are displayed in EUR by default. Partner sites will show local currency at checkout based on your IP / browser locale. You\'re always charged in the partner\'s checkout currency.',
    'page.faq.section.4.heading': 'Can I book from cities other than Sofia?',
    'page.faq.section.4.body': 'Yes. Use the global search bar at the top of any page to set a different departure city. Our catalogue covers 300+ airports worldwide. The AI chat also accepts free-form requests like "flights from Vienna to Bali".',
    'page.faq.section.5.heading': 'What if my chosen city has no airport?',
    'page.faq.section.5.body': 'We automatically route you to the closest commercial airport with a clear "Served by …" hint. For example, asking for Marbella will surface flights to Málaga (AGP, 45 min drive), and Hallstatt routes via Salzburg (SZG).',
    'page.faq.section.6.heading': 'How accurate are AI-suggested dates?',
    'page.faq.section.6.body': 'When you don\'t specify dates, the AI picks the next available window starting at least 14 days from today (no past dates ever). For "weekend" requests it picks a 2-3 night Fri-Sun. You can always override by typing exact dates in the chat.',
    'page.faq.section.7.heading': 'Can I cancel or change my booking?',
    'page.faq.section.7.body': 'Cancellation and change policies are entirely the partner\'s — please read them on their checkout page before paying. Booking.com Flexible rates can usually be cancelled free up to a couple of days before; airline tickets vary widely by fare class.',
    'page.faq.section.8.heading': 'Do you handle group bookings?',
    'page.faq.section.8.body': 'For up to 9 passengers per booking, partners typically handle it via their normal flow. For larger groups (school trips, weddings, corporate offsites) write to us at travelaibg@gmail.com — we can help broker a quote.',
    'page.faq.section.9.heading': 'Is my data safe?',
    'page.faq.section.9.body': 'Yes. We store only what we need (email, password hash, saved trips, alerts). Payments go directly to partners — we never see your card details. Full details are in the Privacy Policy.',
    'page.faq.section.10.heading': 'How can I report a bug or suggest a feature?',
    'page.faq.section.10.body': 'Email travelaibg@gmail.com or use the Contact Us form. We read every message — your feedback shapes our roadmap.',

    // Privacy Policy
    'page.privacy.title': 'Privacy Policy',
    'page.privacy.lead': 'TravelAI ("we", "us") respects your privacy and is committed to protecting your personal data in line with the EU General Data Protection Regulation (GDPR) and Bulgarian Personal Data Protection Act. This policy explains what we collect, why we collect it, and what rights you have.',
    'page.privacy.section.1.heading': '1. Who we are',
    'page.privacy.section.1.body': 'TravelAI is an AI-powered travel concierge that helps you discover and book flights, hotels and packages via our affiliate partners. We operate the website travelaibg.com. Our contact email is travelaibg@gmail.com.',
    'page.privacy.section.2.heading': '2. What data we collect',
    'page.privacy.section.2.body': 'We collect:\n• Account data — email address and (for password-based accounts) a salted bcrypt hash of your password. If you sign in with Google, we receive your name, email and profile picture from Google.\n• Trip data — destinations, dates, traveller counts and other parameters you enter in the AI chat or save to your dashboard.\n• Email-alert subscriptions — origin/destination IATA, threshold price, language.\n• Technical data — IP address, browser type, device, language, referring URL. Used for analytics and abuse prevention.\nWe do NOT collect payment-card details. All bookings happen on the partner\'s checkout — we never see your card.',
    'page.privacy.section.3.heading': '3. Why we collect it (legal bases)',
    'page.privacy.section.3.body': 'We process data on three GDPR bases:\n• Contract — to provide the service you asked for (save trips, send alerts, deliver confirmations).\n• Legitimate interest — to improve the product, prevent fraud, and operate the website.\n• Consent — for marketing emails, where applicable. You can withdraw consent at any time.',
    'page.privacy.section.4.heading': '4. Who we share it with',
    'page.privacy.section.4.body': 'We share data only with the processors needed to run the service:\n• Brevo (Sendinblue) — transactional email delivery.\n• Travelpayouts / Aviasales — flight metadata and affiliate tracking.\n• Booking.com, Hotels.com, Agoda — when you click through to book hotels.\n• MongoDB Atlas — database hosting.\n• Emergent / Google — authentication (only if you use Google sign-in).\nAll processors are GDPR-compliant and bound by Data Processing Agreements.',
    'page.privacy.section.5.heading': '5. How long we keep it',
    'page.privacy.section.5.body': 'Account data: as long as your account is active. You can delete your account anytime — we permanently remove your personal data within 30 days (some anonymised logs may remain for fraud prevention up to 12 months). Saved trips and alerts: until you delete them or close your account.',
    'page.privacy.section.6.heading': '6. Your rights under GDPR',
    'page.privacy.section.6.body': 'You have the right to: (a) access your data, (b) correct it, (c) delete it, (d) restrict processing, (e) data portability, (f) object to processing, (g) lodge a complaint with the Bulgarian Commission for Personal Data Protection (CPDP, www.cpdp.bg). To exercise any of these, email travelaibg@gmail.com.',
    'page.privacy.section.7.heading': '7. Cookies & tracking',
    'page.privacy.section.7.body': 'We use a small number of essential cookies (session, language preference, A/B variant). We don\'t run third-party advertising trackers. Affiliate partners may set their own cookies once you click through to their site — review their cookie policies.',
    'page.privacy.section.8.heading': '8. Changes to this policy',
    'page.privacy.section.8.body': 'We may update this policy occasionally. The "Last updated" date at the bottom always reflects the latest version. Significant changes will be announced via email to active users.',

    // Terms of Service
    'page.terms.title': 'Terms of Service',
    'page.terms.lead': 'By using TravelAI (travelaibg.com) you agree to these terms. Please read them carefully — they include important rights, obligations and limitations.',
    'page.terms.section.1.heading': '1. What TravelAI is',
    'page.terms.section.1.body': 'TravelAI is an information and discovery service. We help you plan trips, find flights and hotels, and compare prices via affiliate partners. We are NOT a travel agent, airline or hotel — we don\'t sell, issue or operate any of the services shown. All actual bookings, payments and traveller-supplier contracts happen on the partner\'s site under the partner\'s terms.',
    'page.terms.section.2.heading': '2. Eligibility',
    'page.terms.section.2.body': 'You must be at least 18 years old to create an account or make a booking through our partners. By using the service you confirm you are.',
    'page.terms.section.3.heading': '3. Your account',
    'page.terms.section.3.body': 'You\'re responsible for keeping your password secure and for all activity under your account. Tell us immediately if you suspect unauthorised access. We may suspend accounts that violate these terms or applicable law.',
    'page.terms.section.4.heading': '4. Pricing and availability',
    'page.terms.section.4.body': 'Prices and availability on TravelAI come from third-party APIs and curated data. They are estimates — final, binding prices appear on the partner\'s checkout, in real time. We cannot guarantee that a price seen on TravelAI will still be available at the partner site. We are not liable for price differences, sold-out inventory or partner-side errors.',
    'page.terms.section.5.heading': '5. Bookings and cancellations',
    'page.terms.section.5.body': 'Every booking is a contract between you and the partner (the airline, hotel, OTA), governed by their terms and cancellation policy. Read them before paying. We don\'t process refunds, changes or claims on the partner\'s behalf — please contact the partner directly.',
    'page.terms.section.6.heading': '6. Acceptable use',
    'page.terms.section.6.body': 'Don\'t use TravelAI to: (a) scrape or republish content without permission, (b) submit false or fraudulent bookings, (c) abuse our AI chat (spam, prompt injection, attempts to extract proprietary prompts), or (d) violate any applicable law. We may rate-limit or ban accounts that do.',
    'page.terms.section.7.heading': '7. Limitation of liability',
    'page.terms.section.7.body': 'TravelAI is provided "as is". To the maximum extent allowed by Bulgarian and EU law, we are not liable for indirect, consequential or punitive damages, lost trips, missed flights, partner-side errors, or any loss exceeding the amount of affiliate commission we earned from your specific booking (typically a few euros). Nothing in these terms limits liability for fraud, wilful misconduct or rights you have under mandatory consumer law.',
    'page.terms.section.8.heading': '8. Intellectual property',
    'page.terms.section.8.body': 'The TravelAI name, logo, AI prompts, code and curated content are our intellectual property. Partner brand names (Booking.com, Aviasales, airlines, hotels) belong to their respective owners. You may not reproduce or commercially exploit our content without written permission.',
    'page.terms.section.9.heading': '9. Governing law',
    'page.terms.section.9.body': 'These terms are governed by Bulgarian law. Any dispute arising out of or in connection with the service will be resolved by the competent courts of Bulgaria, without prejudice to mandatory consumer protections under your country of residence.',

    // Contact page
    'contact.title': 'Contact us',
    'contact.lead': 'Have a question, found a bug, or want to suggest a feature? Drop us a message and we\'ll get back to you within one business day.',
    'contact.emailLabel': 'Email',
    'contact.locationLabel': 'Where we are',
    'contact.responseTime': 'We typically reply within a few hours on European business days. Outside of business hours expect a reply the next morning.',
    'contact.fieldName': 'Your name',
    'contact.fieldNamePlaceholder': 'Jane Doe',
    'contact.fieldEmail': 'Email address',
    'contact.fieldSubject': 'Subject',
    'contact.fieldSubjectPlaceholder': 'How can we help?',
    'contact.fieldMessage': 'Message',
    'contact.fieldMessagePlaceholder': 'Tell us a bit more about what you need…',
    'contact.privacy': 'By submitting you agree we can use the info above to reply to your message. See our Privacy Policy for details.',
    'contact.send': 'Send message',
    'contact.sending': 'Sending…',
    'contact.successTitle': 'Message sent!',
    'contact.successMessage': 'Thanks — we\'ve received your message and will reply shortly.',
    'contact.errorTitle': 'Couldn\'t send your message',
    'contact.errorGeneric': 'Something went wrong. Please try again, or email us directly:',
    'contact.errorNetwork': 'Network error. Please check your connection and try again, or email us directly:',
    'contact.emailFallback': 'travelaibg@gmail.com',
    'contact.validation': 'Please fill in all required fields with a valid email and at least a 5-character message.',
  },
  bg: {
    'header.home': 'Начало',
    'header.flights': 'Полети',
    'header.hotels': 'Хотели',
    'header.packages': 'Пакети',
    'header.saved': 'Запазени',
    'header.profile': 'Профил',
    'header.signIn': 'Вход',
    'header.signOut': 'Изход',
    'header.smartAssistant': 'Умен Пътеводител',
    'hero.title': 'Вашият Личен',
    'hero.aiAssistant': 'AI Пътеводител',
    'hero.subtitle': 'за Всяко Пътуване',
    'hero.description': 'Планирайте пътувания с AI асистент, разглеждайте полети и хотели и създавайте маршрути с няколко дестинации. Потвърдете крайните цени и наличностите при партньорите.',
    'hero.budgetFriendly': 'Икономично',
    'hero.midRange': 'Средна Цена',
    'hero.luxury': 'Луксозно',
    'hero.aiPowered': 'С Изкуствен Интелект',
    'hero.aiPoweredDesc': 'Умни препоръки, съобразени с вашите предпочитания',
    'hero.globalCoverage': 'Глобално Покритие',
    'hero.globalCoverageDesc': 'Достъп до полети и хотели по целия свят',
    'hero.support247': 'Бърза Поддръжка',
    'hero.support247Desc': 'Бърза, лична помощ, когато имате нужда',
    'hero.secure': 'Сигурна Резервация',
    'hero.secureDesc': 'Резервация и плащане при избрания туристически партньор',
    'hero.poweredByAi': 'Със изкуствен интелект',
    'hero.planTripCta': 'Планирай с AI',
    'hero.findDealsCta': 'Намери най-добрите оферти',
    'hero.orBrowseDeals': 'или разгледай офертите за днес',
    'hero.trustedPartners': 'Търсене на оферти чрез',
    // Sprint A2 — editorial chip labels (amber)
    'chip.travelaiPick':     'Избор на TravelAI',
    'chip.aiRecommendation': 'AI Препоръка',
    'chip.priceDrop':        'Спад в цената',
    'chip.editorChoice':     'Избор на редакторите',
    // Sprint B — TrustChip labels (real data only)
    'trust.cheapestToday':   'Най-евтино днес',
    'trust.nonstop':         'Директен',
    'trust.shortTransfer':   'Кратък трансфер',
    'trust.fastest':         'Най-бърз',
    'trust.freeCancel':      'Безплатна отмяна',
    'trust.updatedToday':    'Обновено днес',
    'trust.iata':            'IATA сертифицирано',
    'trust.familyFriendly':  'За семейства',
    'trust.beachfront':      'На плажа',
    'trust.aiPick':          'AI Избор',
    'trust.bestValue':       'Най-добра стойност',
    'trust.mostBooked':      'Най-резервирано',
    'trust.verified':        'Проверено',
    // V4 · Intro Gate (BG)
    'intro.headline': 'Тих кът за',
    'intro.headlineAccent': 'изключителни пътувания',
    'intro.subtitle': 'Вашият личен секретар за пътуванията, за които другите само мечтаят. Полети, хотели и подбрани находки — деликатно подредени.',
    'intro.enter': 'Влезте',
    'intro.footnote': 'Основано в София · Подбрано за изтънчения пътник',
    // V4 · Jarvis Rare Finds (BG)
    'rareFinds.kicker': 'Подбор на Jarvis',
    'rareFinds.title': 'Редки находки',
    'rareFinds.subtitle': 'Тихи открития, които вашият личен секретар е отделил — за пътника, който вече е виждал пощенските картички.',
    'rareFinds.viewAll': 'Виж всички редки находки',
    'rareFinds.footnote': 'Лично подбрано. Без спонсориране.',
    // V5 · Smart Discovery Homepage (BG)
    'discovery.kicker':    'Живо откритие',
    'discovery.title':     'Реални оферти, обновени всеки ден.',
    'discovery.subtitle':  'Наблюдавани от Aviasales цени за двупосочни полети от България. Един клик резервира полет и подходящ хотел.',
    'discovery.deals':     'оферти',
    'discovery.footnote':  'Цени наблюдавани от Aviasales · обновени дневно · потвърдете актуална наличност при резервация.',
    // V5 · Floating AI concierge widget (BG)
    'chat.floatingAria':   'Отвори AI консиерж',
    'chat.floatingKicker': 'Личен консиерж',
    'chat.floatingTitle':  'Разговор с Jarvis',
    'rareFinds.cat.all':       'Всички',
    'rareFinds.cat.cities':    'Редки градове',
    'rareFinds.cat.retreats':  'Бутикови оазиси',
    'rareFinds.cat.cinematic': 'Кинематографични бягства',
    'rareFinds.partner.aviasales': 'Виж цени в Aviasales',
    'rareFinds.partner.booking':   'Резервирай в Booking.com',
    // Editorial city cards (BG regions + notes)
    'rareFinds.valletta.region':  'Малта',
    'rareFinds.valletta.note':    'Барокови палацо-апартаменти на улица „Република" — пристигнете преди тълпите от фериботите.',
    'rareFinds.tbilisi.region':   'Грузия',
    'rareFinds.tbilisi.note':     'Резбовани дървени балкони в Сололаки — най-подценяваната кавказка столица.',
    'rareFinds.ljubljana.region': 'Словения',
    'rareFinds.ljubljana.note':   'Пешеходен стар град край реката — Виена в миниатюр, без опашки.',
    'rareFinds.faro.region':      'Алгарве · Португалия',
    'rareFinds.faro.note':        'Крайбрежни къщички при Понта да Пиедаде — тези, които пътеводителите тихо са забравили.',
    'rareFinds.salzburg.region':  'Австрия',
    'rareFinds.salzburg.note':    'Единствен апартамент над старите бижутери на Гетрайдегасе — на крачки от Моцарт, тих през нощта.',
    'rareFinds.bilbao.region':    'Страна на баските · Испания',
    'rareFinds.bilbao.note':      'Дискретен 18-стаен хотел зад Гугенхайм — пинтхосите са на две пресечки.',
    'rareFinds.kyoto.region':     'Япония',
    'rareFinds.kyoto.note':       'Риокан в Хигашияма през ноември — сезонът на кленовете без туристическите автобуси.',
    'rareFinds.marrakech.region': 'Мароко',
    'rareFinds.marrakech.note':   'Риад в квартала на Касбата — фонтани в двор, чай на терасата, без рецепция.',
    'rareFinds.reykjavik.region': 'Исландия',
    'rareFinds.reykjavik.note':   'Дизайн хотел на две пресечки от Харпа — сияние през зимата, бели нощи през лятото.',
    // V4 · Полет → Хотел кръстосана препоръка (BG)
    'crossSell.hotel.title': 'Да уредя ли и настаняването?',
    'crossSell.hotel.body': 'За тези дати има малка селекция от подбрани хотели на дестинацията.',
    'crossSell.hotel.cta': 'Подберете ми хотел',
    // V4 · София → Света identity mark
    'hero.identityMark': 'София → Света',
    // Sprint E · Mobile bottom nav (BG) — nav.home/flights/hotels/saved
    // already defined above; only 'nav.chat' is new.
    'nav.chat':    'Чат',
    'hero.titlePrefix': 'Твоят личен',
    'hero.titleAccent': 'AI пътеводител',
    'hero.titleSuffix': 'за всяко пътуване',
    'hero.eyebrow': 'AI асистент · Маршрути · Партньорски търсения',
    // Homepage section headers (BG)
    'home.section.aiKicker': 'AI Консиерж',
    'home.section.aiTitle': 'Започни с разговор, не с търсене.',
    'home.section.aiSubtitle': 'Кажи ни настроението, бюджета или примерни дати — ние сравняваме офертите и връщаме готов план.',
    'home.section.gemKicker': 'Редакторски избор',
    'home.section.gemTitle': 'Днешният скрит бисер',
    'home.section.gemSubtitle': 'Вдъхновение за по-малко позната дестинация. Проверете сезона и изискванията за влизане преди пътуване.',
    'home.section.dealsKicker': 'Пазар на живо',
    'home.section.dealsTitle': 'Реални цени от реални партньори.',
    'home.section.dealsSubtitle': 'Разгледайте партньорски търсения за полети и хотели. Потвърдете крайната цена и условията преди резервация.',
    'hero.stat.destinations': 'Планер за пътувания',
    'hero.stat.airlines': 'Езици',
    'hero.stat.partners': 'Маршрути',
    'hero.stat.fees': 'Такса за планиране',
    'hero.proof.routes': 'Организирай пътуване · провери цените',
    'hero.proof.aiCopilot': 'AI консултант · план за секунди',
    'hero.proof.bilingual': 'Планиране на български и английски',
    'hero.proof.secure': 'Резервация и плащане при доставчика',
    'home.partnerLogosCaption': 'Търсете полети и хотели чрез',
    // Phase-2 — HowItWorks section
    'home.howItWorksKicker': 'Защо TravelAI',
    'home.howItWorksTitle': 'По-добър начин да планираш всяко пътуване',
    'home.howItWorksSub': 'Един ясен план, с окончателни оферти при доставчика.',
    'home.howItWorks.ask.title': 'Питай на нормален език',
    'home.howItWorks.ask.desc': 'Кажи какво искаш на български или английски — дати, бюджет, стил. Без досадни филтри.',
    'home.howItWorks.compare.title': 'AI сравнява за секунди',
    'home.howItWorks.compare.desc': 'AI помага да подредите дестинации, дати и предпочитания, след което можете да проверите офертите и наличността при партньорите.',
    'home.howItWorks.book.title': 'Резервирай при партньор, на когото имаш доверие',
    'home.howItWorks.book.desc': 'TravelAIBG те насочва към доставчик на полети или хотели, където проверяваш условията и завършваш резервацията.',
    // Phase-2 — HomepageFAQ section
    'home.faqKicker': 'Често задавани въпроси',
    'home.faqTitle': 'Всичко, което може да попиташ преди резервация',
    'home.faq.pricing.q': 'Реални и актуални ли са цените, които виждам?',
    'home.faq.pricing.a': 'Показаните в TravelAIBG цени може да са кеширани или ориентировъчни. Наличността и крайната сума се потвърждават на сайта на авиокомпанията или партньора преди плащане.',
    'home.faq.safety.q': 'Сигурно ли е да резервирам през TravelAI?',
    'home.faq.safety.a': 'TravelAIBG не приема плащанията за полета или хотела. Преминаваш към доставчика и там проверяваш крайната цена, наличността, сигурността на плащането, анулациите и потвърждението.',
    'home.faq.fees.q': 'Има ли скрити такси или комисиони за мен?',
    'home.faq.fees.a': 'TravelAIBG засега няма отделна такса за планиране. Доставчикът определя крайната сума, включително таксите и данъците. Партньорски приходи зависят от конкретната програма и не са гарантирани при клик.',
    'home.faq.aiTrust.q': 'Мога ли да се доверя на AI препоръките?',
    'home.faq.aiTrust.a': 'AI предложенията помагат да откриеш дестинации и да подредиш маршрут. Някои предложения и цени са ориентировъчни. Проверявай полетите, хотелите и крайните суми при доставчика преди плащане.',
    'home.faq.cancellation.q': 'А отмени и промени?',
    'home.faq.cancellation.a': 'Правилата за анулация, промени, багаж, такси и плащане зависят от доставчика и тарифата. Прочети действителните условия в сайта на доставчика преди резервация.',
    'home.faq.multiLang.q': 'TravelAI на български ли е?',
    'home.faq.multiLang.a': 'Да. Интерфейсът поддържа български и английски. Сайтовете на доставчиците, потвържденията и поддръжката може да бъдат на други езици.',
    'chat.title': 'AI Пътепис Конциерж',
    'chat.subtitle': 'AI планиране · провери крайните цени',
    'chat.welcome': 'Здравей! Кажи ми дестинация, дати, предпочитания или бюджет. Ще ти помогна с план и търсене при доставчиците, като крайните цени и наличността се потвърждават при тях.',
    // Change 3 · Page-contextual welcome messages (BG — user-provided copy)
    'chat.welcome.home':        'Здравей! Аз съм твоят личен TravelAI асистент. Мога да ти помогна да намериш евтини полети, хотели или да планираш цялото пътуване. Накъде мечтаеш да пътуваш?',
    'chat.welcome.flights':     'Търсиш полет? Кажи ми откъде летиш, накъде и кога — ще намеря най-добрите цени.',
    'chat.welcome.hotels':      'Търсиш хотел? Кажи ми дестинацията, датите и броя гости — ще намеря наличните опции.',
    'chat.welcome.packages':    'Искаш да планираш цялото пътуване? Кажи ми дестинация, дати и бюджет — ще наредим полет, хотел и трансфер.',
    'chat.welcome.destination': 'Планираш пътуване? Мога да ти помогна да намериш полет и хотел и да наредиш всичко на едно място.',
    'chat.placeholder': 'Опишете идеалното си пътуване... (напр. "7 дни в Италия за 2 души, бюджет 1500 €")',
    'chat.liveIndicator': 'Онлайн',
    'chat.listening': 'слушам…',
    'chat.saveTrip': 'Запази',
    'chat.shareTrip': 'Сподели пътуването',
    'chat.saving': 'Запазва…',
    'auth.signIn': 'Вход',
    'auth.signUp': 'Регистрация',
    'auth.email': 'Имейл Адрес',
    'auth.password': 'Парола',
    'auth.welcomeBack': 'Добре дошли отново',
    'auth.createAccount': 'Създайте своя акаунт',
    'auth.noAccount': 'Нямате акаунт?',
    'auth.hasAccount': 'Вече имате акаунт?',
    'auth.signingIn': 'Влизане...',
    'auth.signingUp': 'Създаване на акаунт...',
    'auth.fullName': 'Пълно Име',
    'nav.home': 'Начало',
    'nav.flights': 'Полети',
    'nav.hotels': 'Хотели',
    'nav.packages': 'Пакети',
    'nav.saved': 'Запазени',
    'common.loading': 'Зареждане...',
    'common.error': 'Грешка',
    'common.success': 'Успех',
    'common.search': 'Търсене',
    'common.cancel': 'Отказ',
    'common.close': 'Затвори',
    'common.from': 'От',
    'common.to': 'До',
    'common.total': 'Общо',
    'common.night': 'нощ',
    'common.nights': 'нощи',
    'common.back': 'Назад',
    'common.guest': 'гост',
    'common.guests': 'гости',
    'common.room': 'стая',
    'common.rooms': 'стаи',
    'common.checkin': 'Настаняване',
    'common.checkout': 'Освобождаване',
    'common.departure': 'Заминаване',
    'common.return': 'Връщане',
    'common.adults': 'Възрастни',
    'common.children': 'Деца',
    'common.direct': 'Директен',
    'common.stop': 'спирка',
    'common.stops': 'спирки',
    'common.viewDeal': 'Виж офертата',
    'common.explore': 'Разгледай',

    // Best Offers / Home
    'home.todaysTopDeals': 'Най-добрите оферти днес',
    'home.todaysTopDealsSub': 'Полети на живо и подбрани пакети — специално за вас',
    'home.todaysTopDealsKicker': 'Обновено преди минути',
    'home.searchFlights': 'Търси полети',
    'home.searchHotels': 'Търси хотели',
    'home.todaysBestFlightDeals': 'Най-добрите полети днес',
    'home.featuredPackages': 'Препоръчани пакети',
    'home.livePrices': 'Наблюдавани цени · провери крайното предложение',
    'home.partnerNote': 'Офертите се обновяват редовно · Цените и наличността идват от партньорски платформи (Aviasales, Booking.com) · Може да варират при резервация',
    'home.noDeals': 'В момента няма налични оферти. Опитайте търсене на полети.',
    'home.flightDate': 'Дата на полета',
    'home.secureBooking': 'Сигурна резервация',
    'home.poweredBy': 'Чрез глобални пътнически партньори',
    'home.bestPrice': 'Сравнете оферти при партньорите',
    'home.trustedFooter': 'Планирайте с AI · Търсете полети и хотели · Резервирайте при доставчика',
    'home.popular': 'ПОПУЛЯРНО',
    'home.new': 'НОВО',
    'home.exclusive': 'ЕКСКЛУЗИВНО',
    'home.hot': 'ТОП ОФЕРТА',
    'home.bestSeller': 'Най-продавано',
    'home.limitedTime': 'Ограничено време',
    'home.premiumPick': 'Премиум избор',

    // Flight search
    'flights.title': 'Намерете идеалния полет',
    'flights.subtitle': 'Сравнете полети от мрежата ни от доверени авиокомпании',
    'flights.findDeals': 'Намери най-добрите цени',
    'flights.prefillTitle': 'Попълнено от AI чата:',
    'discover.tabs.all': 'Всички',
    'discover.flights.tabsLabel': 'Филтър по категория полети',
    'discover.hotels.tabsLabel': 'Филтър по категория хотели',
    'destination.missingKey': 'Не е избрана дестинация.',
    'flights.fillFields': 'Моля, попълнете всички задължителни полета',
    'flights.sameAirports': 'Произходът и дестинацията не могат да съвпадат',
    'flights.errorSearching': 'Грешка при търсене на полети',
    'flights.found': 'намерени полета',
    'flights.empty': 'Въведете маршрут и дати, за да търсите полети',
    'flights.reserve': 'Резервирай',
    'flights.from': 'Откъде',
    'flights.to': 'Накъде',
    'flights.typePlaceholder': 'Въведете град или летище...',
    'flights.departure': 'Заминаване',
    'flights.return': 'Връщане',
    'flights.passengersClass': 'Пътници и класа',
    'flights.passengers': 'Пътници',
    'flights.adults': 'Възрастни',
    'flights.class': 'Класа',
    'flights.travelClass': 'Класа',
    'flights.economy': 'Икономична',
    'flights.premiumEconomy': 'Премиум икономична',
    'flights.business': 'Бизнес',
    'flights.first': 'Първа',
    'flights.firstClass': 'Първа класа',
    // Sprint D · Search Experience Overhaul (BG)
    'flights.searchingTitle': 'Търсим най-добрите оферти…',
    'flights.searchingSubtitle': 'Сравняваме 700+ авиокомпании на живо',
    'flights.countOne': 'полет',
    'flights.countMany': 'полета',
    'flights.filtersTitle': 'Уточни полетите',
    'flights.sort.best': 'Най-добри',
    'flights.sort.cheapest': 'Най-евтини',
    'flights.sort.fastest': 'Най-бързи',
    'flights.sort.direct': 'Директни първи',
    'flights.filter.stopsTitle': 'Междинни спирания',
    'flights.filter.anyStops': 'Всякакви',
    'flights.filter.directOnly': 'Само директни',
    'flights.filter.maxOneStop': 'Макс. 1 спиране',
    'flights.filter.priceTitle': 'Ценови диапазон',
    'flights.filter.airlinesTitle': 'Авиокомпании',
    'flights.filteredOut.title': 'Няма полети с тези филтри',
    'flights.filteredOut.body': 'Опитай да разхлабиш някой филтър.',
    'flights.filteredOut.cta': 'Изчисти филтрите',
    'flights.empty.title': 'Не открихме полети',
    'flights.empty.body': 'Опитай съседни летища, по-широки дати или друга дестинация.',
    'flights.empty.cta': 'Редактирай търсенето',

    // Hotel search
    'hotels.title': 'Намерете идеалния хотел',
    'hotels.subtitle': 'Сравнете хотели от нашите доверени глобални партньори',
    'hotels.search': 'Търси хотели',
    'hotels.findDeals': 'Намери най-добрите цени',
    'hotels.prefillTitle': 'Попълнено от AI чата:',
    'hotels.fillFields': 'Моля, попълнете всички задължителни полета',
    'hotels.errorSearching': 'Грешка при търсене на хотели',
    'hotels.found': 'намерени хотела',
    'hotels.empty': 'Въведете дестинация и дати, за да търсите хотели',
    'hotels.reserve': 'Резервирай',
    'hotels.destination': 'Дестинация',
    'hotels.typeCity': 'Въведете град...',
    'hotels.noResults': 'Няма намерени дестинации. Опитайте: Барселона, Париж, Рим...',
    'hotels.checkIn': 'Настаняване',
    'hotels.checkOut': 'Напускане',
    'hotels.guestsRooms': 'Гости и стаи',
    'hotels.adults': 'Възрастни',
    'hotels.children': 'Деца',
    'hotels.rooms': 'Стаи',
    'hotels.guests': 'гости',
    'hotels.room': 'стая',
    'hotels.child': 'Дете',
    'hotels.childrenBirthHint': 'Дата на раждане (по избор — за точна тарифа за бебе/дете):',
    'hotels.foundCount': 'намерени хотела',
    // Sprint D · Search Experience Overhaul (BG)
    'hotels.searchingTitle': 'Сравняваме хиляди хотели…',
    'hotels.searchingSubtitle': 'Актуална наличност от Booking.com и партньори',
    'hotels.countOne': 'хотел',
    'hotels.countMany': 'хотела',
    'hotels.filtersTitle': 'Уточни хотелите',
    'hotels.sort.recommended': 'Препоръчани',
    'hotels.sort.cheapest': 'Най-евтини',
    'hotels.sort.topRated': 'Най-високо оценени',
    'hotels.sort.luxury': 'Луксозни първи',
    'hotels.filter.ratingTitle': 'Оценка от гости',
    'hotels.filter.ratingAny': 'Всякаква',
    'hotels.filter.priceTitle': 'Цена / нощ',
    'hotels.filter.typeTitle': 'Тип имот',
    'hotels.filter.amenitiesTitle': 'Удобства и политики',
    'hotels.filter.freeCancel': 'Безплатна отмяна',
    'hotels.filteredOut.title': 'Няма хотели с тези филтри',
    'hotels.filteredOut.body': 'Опитай да разхлабиш някой филтър.',
    'hotels.filteredOut.cta': 'Изчисти филтрите',
    'hotels.empty.title': 'Не открихме хотели',
    'hotels.empty.body': 'Опитай съседни градове, по-дълъг престой или по-малко гости.',
    'hotels.empty.cta': 'Редактирай търсенето',

    // Dashboard / Profile
    'dashboard.welcomeBack': 'Добре дошли отново!',
    'dashboard.signInRequired': 'Влезте, за да видите профила си',
    'dashboard.signInDesc': 'Достъп до запазените пътувания, резервации и история',
    'dashboard.savedTrips': 'Запазени пътувания',
    'dashboard.bookings': 'Резервации',
    'dashboard.chatSessions': 'Чат сесии',
    'dashboard.tab.overview': 'Преглед',
    'dashboard.tab.trips': 'Пътувания',
    'dashboard.tab.bookings': 'Резервации',
    'dashboard.recentActivity': 'Скорошна активност',
    'dashboard.noActivity': 'Няма скорошна активност. Започнете да чатите с AI асистента!',
    'dashboard.noTrips': 'Няма запазени пътувания. Започнете да планирате и запазете любимите си!',
    'dashboard.yourBookings': 'Вашите резервации',
    'dashboard.noBookings': 'Няма резервации. Намерете перфектното пътуване и резервирайте днес!',
    'dashboard.deleteConfirm': 'Сигурни ли сте, че искате да изтриете това пътуване?',
    'dashboard.tripDeleted': 'Пътуването е изтрито успешно',
    'dashboard.deleteFailed': 'Неуспешно изтриване',
    'dashboard.unknownDest': 'Неизвестна дестинация',
    'dashboard.savedAtPrefix': 'Запазено',
    'dashboard.bookingIdLabel': 'Номер на резервация',
    'dashboard.hotelsCount': 'хотела',
    'dashboard.flightsCount': 'полета',

    // Trip Comparison
    'compare.title': 'Сравни пътувания',
    'compare.subtitle': 'Изберете до 3 пътувания за сравнение едно до друго',
    'compare.empty.title': 'Все още няма запазени пътувания',
    'compare.empty.body': 'Започнете чат с AI асистента, за да планирате пътувания, след което ги запазете за сравнение.',
    'compare.selectAtLeast2': 'Изберете поне 2 пътувания, за да ги сравните едно до друго',
    'compare.maxThree': 'Можете да сравнявате до 3 пътувания едновременно',
    'compare.signInToCompare': 'Влезте, за да запазвате и сравнявате пътувания',
    'compare.heading': 'Сравнение на пътувания',
    'compare.notSpecified': 'Не е посочено',
    'compare.noHotels': 'Няма хотели',
    'compare.noFlights': 'Няма полети',
    'compare.col.feature': 'Характеристика',
    'compare.col.duration': 'Продължителност',
    'compare.col.budgetType': 'Бюджет',
    'compare.col.hotels': 'Хотели',
    'compare.col.flights': 'Полети',
    'compare.budget.budget': 'Бюджетен',
    'compare.budget.standard': 'Стандартен',
    'compare.budget.luxury': 'Луксозен',

    // Booking flow
    'booking.tripSummary': 'Резюме на пътуването',
    'booking.reviewBeforeConfirm': 'Прегледайте пътуването преди отваряне на партньора',
    'booking.completeReservation': 'Завърши резервацията',
    'booking.secureGuarantee': 'Резервация при партньора',
    'booking.freeCancellation': 'Безплатна отмяна',
    'booking.secureBookingItem': 'Сигурна резервация',
    'booking.bestPriceGuarantee': 'Крайната цена се потвърждава при партньора',
    'booking.verifiedSecure': 'Проверете крайната цена и условията при партньора',
    'booking.securingDeal': 'Отваряме сайта на партньора...',
    'booking.holdOn': 'Моля, изчакайте докато подготвим резервацията',
    'booking.partnerNote': 'Резервацията се извършва чрез доверени партньори (Aviasales, Booking.com). Цените и наличността идват директно от тези платформи.',
    'booking.estimateNote': 'Прогнозна сума · крайната цена се определя от партньора',
    // Destination detail modal
    'destination.loading': 'Зареждане на дестинацията...',
    'destination.unavailable.title': 'Все още няма налични данни за тази дестинация',
    'destination.unavailable.body': 'Показваме пакети само ако имаме реални данни за полети и хотели. Разгледайте поддържаните дестинации в страниците Полети и Хотели.',
    'destination.unavailable.cta': 'Разгледай дестинации',
    'destination.flights': 'Полети',
    'destination.hotels': 'Настаняване',
    'destination.insiderTips': 'Местни съвети',
    'destination.viewFlights': 'Виж полети',
    'destination.compareDistributors': 'Сравнете цените при нашите партньори — изберете най-ниската:',
    'destination.reserveOnPartner': 'Резервирай в {partner}',
    'destination.tier.cheap': 'Икономичен',
    'destination.tier.balanced': 'Балансиран',
    'destination.tier.premium': 'Премиум',
    'destination.tier.budget': 'Бюджетен',
    'destination.tier.standard': 'Среден клас',
    'destination.tier.luxury': 'Луксозен',
    // Discovery / Inspiration grid (Полети & Хотели)
    'discover.priceLabel': 'От',
    'discover.cta': 'Виж',
    'discover.badge.hot': 'Горещо',
    'discover.badge.bestValue': 'Най-добра цена',
    'discover.badge.popular': 'Популярно',
    'discover.badge.luxury': 'Лукс',
    'discover.badge.premium': 'Премиум',
    'discover.badge.quick': 'Кратко',
    'discover.badge.romantic': 'Романтично',
    'discover.badge.family': 'Семейно',
    'discover.badge.smartDeal': 'Умна оферта',
    'discover.flights.smartDeals.title': 'Умни оферти',
    'discover.flights.smartDeals.subtitle': 'Маршрути с необичайно изгодни цени точно сега',
    'discover.flights.intro.title': 'Накъде следва?',
    'discover.flights.intro.subtitle': 'Подбрани маршрути, които пътуващите ни обичат сега',
    'discover.flights.trending.title': 'Популярни дестинации',
    'discover.flights.trending.subtitle': 'Най-търсените маршрути тази седмица',
    'discover.flights.cheap.title': 'Евтини полети',
    'discover.flights.cheap.subtitle': 'Най-ниски цени от вашия град',
    'discover.flights.weekend.title': 'Уикенд бягства',
    'discover.flights.weekend.subtitle': 'Кратки полети · идеално за 2-3 нощувки',
    'discover.flights.luxury.title': 'Премиум приключения',
    'discover.flights.luxury.subtitle': 'Дълги полети, бизнес класа и слънце целогодишно',
    'discover.hotels.intro.title': 'Места, които ще обикнете',
    'discover.hotels.intro.subtitle': 'Подбрани градове, всички категории, мигновено сравнение',
    'discover.hotels.budget.title': 'Бюджетни препоръки',
    'discover.hotels.budget.subtitle': 'Достъпни градове · 3★ хотели от €40/нощ',
    'discover.hotels.midRange.title': 'Среден клас',
    'discover.hotels.midRange.subtitle': 'Стилни 4★ хотели за модерния пътник',
    'discover.hotels.luxury.title': 'Лукс настаняване',
    'discover.hotels.luxury.subtitle': '5★ резорти, апартаменти и плажни ескейпи',
    'discover.hotels.romantic.title': 'Романтични ескейпи',
    'discover.hotels.romantic.subtitle': 'Градове за двама',
    'discover.hotels.family.title': 'Семейни дестинации',
    'discover.hotels.family.subtitle': 'Градове, плажове и култура за цялото семейство',
    'discover.trust.title': 'Натиснете карта · ние ще сравним партньорите вместо вас',
    'discover.trust.subtitle': 'Aviasales · Booking.com · Hotels.com · Agoda · Trivago — сравняваме реални цени, за да резервирате уверено.',

    'booking.verifiedPartner': 'Доверен партньор',
    'booking.instantConfirm': 'Незабавно потвърждение',
    'booking.tapToContinue': 'Натиснете, за да продължите към резервацията',
    // iter80 — premium RedirectOverlay copy
    'redirect.heading': 'Резервация с официален партньор',
    'redirect.opening': 'Отваряме защитеното плащане…',
    'redirect.route': 'Маршрут',
    'redirect.destination': 'Дестинация',
    'redirect.hotel': 'Хотел',
    'redirect.airline': 'Авиокомпания',
    'redirect.item': 'Избор',
    'redirect.dates': 'Дати',
    'redirect.stay': 'Престой',
    'redirect.travellers': 'Пътници',
    'redirect.displayedPrice': 'Показана цена',
    'redirect.priceCaveat': 'Крайната цена се потвърждава при партньора преди плащане.',
    'redirect.trust.officialPartner': 'Външен доставчик — провери условията',
    'redirect.trust.securePayment': 'Защитено плащане при партньора',
    'redirect.trust.noHiddenFees': 'Без скрити такси или надценки',
    // Sprint B — save-this-deal recovery on RedirectOverlay
    'redirect.saveThisDeal': 'Запази офертата',
    'redirect.saved':        'Запазено',
    'redirect.priceAlert':   'Известие за цена',

    // Chat extras
    'chat.send': 'Изпрати',
    'chat.thinking': 'Размишлявам...',
    'chat.suggestionsTitle': 'Опитайте да попитате:',
    'chat.book': 'Резервирай',
    'chat.error': 'Извинявайте, имам проблем с отговора. Моля, опитайте отново след малко.',
    'chat.streamAnalyzing': 'Анализирам заявката ти…',
    'chat.streamBuildingPlan': 'Изграждам твоя маршрут…',
    'chat.streamFinalizing': 'Финализирам детайлите…',
    'chat.priceDisclaimer': 'Цените са от партньорски платформи · може да варират',
    'chat.chip.cheapest': 'Най-евтин',
    'chat.chip.fastest': 'Най-бърз',
    'chat.chip.direct': 'Директен',
    'chat.chip.fewestStops': 'Най-малко спирки',
    'chat.chip.topRated': 'Най-висок рейтинг',
    'chat.chip.bestValue': 'Най-добра стойност',
    'chat.chip.centrallyLocated': 'Централно',
    'chat.chip.freeCancellation': 'Безплатна отмяна',
    'chat.chip.breakfast': 'Закуска',
    // Phase-3 · CardMetaLine
    'chat.meta.why.topRated':          'Топ избор по оценка',
    'chat.meta.why.bestValue':         'Най-добра стойност',
    'chat.meta.why.centrallyLocated':  'Централно разположен',
    'chat.meta.why.freeCancellation':  'Гъвкава отмяна',
    'chat.meta.why.breakfast':         'Със закуска',
    'chat.meta.why.cheapest':          'Най-ниска цена днес',
    'chat.meta.why.fastest':           'Най-кратко пътуване',
    'chat.meta.why.direct':            'Директен полет',
    'chat.meta.why.fewestStops':       'Най-малко спирки',
    'chat.meta.for.families':          'Подходящ за семейства',
    'chat.meta.for.couples':           'Подходящ за двойки',
    'chat.meta.for.business':          'Подходящ за бизнес',
    'chat.meta.for.solo':              'Подходящ за соло пътуване',
    'chat.meta.for.special':           'Подходящ за специален престой',
    'chat.meta.for.timePressed':       'Идеално ако цените времето',
    'chat.meta.for.budget':            'Идеално с бюджет',
    'chat.meta.what.hotel':            'Продължавате в {partner} — крайната цена се показва на плащане',
    'chat.meta.what.hotelFallback':    'Резервацията завършва при нашия проверен хотелски партньор',
    'chat.meta.what.flightFallback':   'Продължавате в Aviasales — цената се фиксира преди плащане',
    'chat.meta.what.transport':        'Продължавате в {partner} — проверен трансферен партньор',
    'chat.meta.what.transportFallback':'Резервацията завършва при нашия проверен транспортен партньор',
    'chat.style.budget': 'Бюджетно',
    'chat.style.romantic': 'Романтично',
    'chat.style.adventure': 'Приключение',
    'chat.style.family': 'Семейно',
    'chat.style.nightlife': 'Нощен живот',
    'chat.style.luxury': 'Лукс',
    'chat.transports': 'Наземен транспорт',
    'chat.transportCta': 'Виж опции',
    'chat.alternativeLabel': 'Умна алтернатива',
    'chat.flightOptions': 'Опции за полети',
    'chat.hotelOptions': 'Опции за хотели',
    'chat.savedTrip': 'Пътуването е запазено!',
    'chat.signinToSave': 'Моля, влезте, за да запазите пътуване',
    'chat.errorSending': 'Изпращането се провали. Опитайте отново.',
    'chat.starterTitle': 'Изберете стил, за да започнем',
    'chat.starter1': 'Изненадай ме този уикенд',
    'chat.starter2': 'Семейно пътуване с деца под 2000€',
    'chat.starter3': 'Романтично бягство за двама',
    'chat.starter4': 'Плажна почивка на изгодна цена',
    'chat.starter5': 'Луксозна годишнина',
    'chat.starter6': 'Скрита перла под 1000€',
    'chat.starterShowMore': 'Покажи още идеи',
    'chat.alternativesTitle': 'Други варианти за разглеждане',
    'chat.alternativesPlan': 'Планирай',
    'chat.starterShowLess': 'Покажи по-малко',
    'chat.insiderTips': 'Инсайдерски съвети от вашия консиерж',
    'chat.bestTime': 'Най-добро време',
    'chat.localGem': 'Местно бижу',
    'chat.thinkingFlights': 'Сканирам живи полети от вашето летище…',
    'chat.thinkingHotels': 'Сравнявам централни хотели…',
    'chat.thinkingDeals': 'Търся най-добрите сделки в реално време…',
    'chat.thinkingCrafting': 'Изработвам вашия премиум маршрут…',
    // Quick-action chips & trip summary & message actions
    'chat.quickCheaper': 'По-евтино',
    'chat.quickLuxe': 'Луксозни опции',
    'chat.quickDates': 'Други дати',
    'chat.quickAddCity': 'Добави град',
    'chat.quickShorter': 'По-кратко пътуване',
    'chat.quickFamily': 'Подходящо за семейства',
    'chat.quickDifferentHotels': 'Други хотели',
    'chat.quickDifferentFlights': 'Други полети',
    'chat.quickDifferentCity': 'Друг град',
    'chat.refineTitle': 'Прецизирай плана:',
    'chat.summaryTitle': 'Вашето пътуване в процес',
    'chat.summaryTotal': 'Прогнозна сума',
    'chat.summaryTravelers': 'Пътници',
    'chat.summaryClear': 'Започни отначало',
    'chat.jumpToBookings': 'Към резервация',
    'chat.copy': 'Копирай',
    'chat.copied': 'Копирано!',

    // Package detail modal
    'package.duration': 'Продължителност',
    'package.rating': 'Рейтинг',
    'package.fromPrice': 'От',
    'package.highlights': 'Какво включва',
    'package.suggestedFlight': 'Предложен полет',
    'package.suggestedHotel': 'Предложен хотел',
    'package.perNight': 'на нощ',
    'package.bookEntire': 'Резервирай целия пакет',
    'package.bookEntireSub': 'Отваря полет и хотел в нови раздели',
    'package.optionalExtensions': 'Допълнителни опции',
    'package.optionalNote': 'Допълнителна опция — резервира се отделно',
    'package.exploreCity': 'Разгледай',
    'package.partnerNote': 'Резервацията се завършва чрез доверени партньори (Aviasales, Booking.com). Цените може да варират в зависимост от наличността.',
    'package.estimatedTotal': 'Прогнозен общ',
    'package.timeline': 'Времева линия на пътуването',
    'package.returnLeg': 'Полет за връщане',
    'package.priceDisclaimer': 'Прогнозен диапазон. Цените може да варират в зависимост от наличността.',
    'package.startSequentialBooking': 'Започни последователна резервация',
    'package.customizeWithAI': 'Персонализирай маршрута с AI',
    'package.aiCustomizePrompt': 'Искам да персонализирам пакета {name} ({nights} нощувки). Помогни ми да адаптирам градовете, датите и продължителността на престоя според моите предпочитания.',
    'package.aiCustomizeIntro': 'Готов съм с вашия {name} за персонализиране! Нека ви задам 3 бързи въпроса, за да го направим перфектен:',
    'package.aiCustomizeQ1': '1. 🏨 Хотели: Предпочитате бюджет (€40-70/нощ), комфорт (€80-150) или нещо специално (€150+)?',
    'package.aiCustomizeQ2': '2. 📅 Дати: Предложените дати са {dates}. Подходят ли, или да ги променим?',
    'package.aiCustomizeQ3': '3. 🔄 Дестинации: Пакетът включва {cities}, с опционални спирки в {extras}. Желаете ли да заменим или добавим някоя?',
    'package.aiCustomizeClose': 'След потвърждение ще ви покажа целия пакет с актуални цени, полети, хотели и линкове за резервация — всичко на едно място.',
    'package.aiCustomizeDatesFlex': 'гъвкави',
    'package.stepper.stepLabel': 'Стъпка {current} от {total}',
    'package.stepper.flightCta': 'Намери този полет',
    'package.stepper.partnerNote': 'Всяка резервация се отваря в нов раздел. Натиснете Следващ сегмент, когато сте готови.',
    'package.stepper.next': 'Следващ сегмент',
    'package.stepper.prev': 'Предишен',
    'package.stepper.finish': 'Завърши плана',
    'package.stepper.empty': 'Няма налични сегменти за резервация в този пакет в момента. Моля, опитайте по-късно.',
    'trip.shareText': 'Виж това пътуване, което планирам в TravelAI',
    'trip.copyPrompt': 'Копирай този линк за споделяне:',
    'trip.sharedHeading': 'Споделен план за пътуване',
    'trip.flight': 'Полет',
    'trip.hotel': 'Хотел',
    'trip.cta.title': 'Харесва ли ти?',
    'trip.cta.body': 'Планирай това пътуване сам за 60 секунди — същият маршрут, твои дати.',
    'trip.cta.plan': 'Планирай това пътуване',
    'trip.cta.share': 'Сподели пътуването',
    'trip.cta.copied': 'Линкът е копиран!',
    'trip.footer': 'Powered by TravelAI · Всички резервации се завършват чрез доверени партньори (Aviasales, Booking.com).',
    'trip.notFound.title': 'Този план за пътуване не е наличен',
    'trip.notFound.body': 'Линкът може да е остарял. Разгледай нашите подбрани пакети, за да започнеш планирането на следващото си приключение.',
    'trip.notFound.browse': 'Виж пакетите',
    'search.flightPlaceholder': 'Търси всеки град в света…',
    'search.hotelPlaceholder': 'Къде искате да отседнете?',
    'search.flightCta': 'Търси полети',
    'search.hotelCta': 'Търси настаняване',
    'search.tap': 'Натисни, за да избереш',
    'alerts.title': 'Получи известие, когато цените за {city} паднат',
    'alerts.subtitle': 'Ще ти изпратим имейл в момента, в който билетът падне под твоята цел. Без спам.',
    'alerts.emailLabel': 'Имейл',
    'alerts.thresholdLabel': 'Известявай ме под (€) за {city}',
    'alerts.submitCta': 'Активирай известие',
    'alerts.privacyNote': 'Използваме имейла само за това известие. Можеш да се отпишеш по всяко време.',
    'alerts.successTitle': 'Готово!',
    'alerts.successBody': 'Ще ти изпратим имейл, когато цената падне под целта ти.',
    'alerts.invalidEmail': 'Моля, въведи валиден имейл.',
    'alerts.error': 'Нещо се обърка. Моля, опитай отново.',

    // Footer
    'footer.brandTagline': 'Революционизираме планирането на пътувания с изкуствен интелект. Открийте невероятни дестинации, намерете най-добрите цени и създайте незабравими спомени — от София до всяка точка на света.',
    'footer.quickLinks': 'Бързи връзки',
    'footer.partners': 'Нашите партньори',
    'footer.support': 'Поддръжка',
    'footer.helpCenter': 'Помощен център',
    'footer.contactUs': 'Свържете се с нас',
    'footer.faq': 'Често задавани въпроси',
    'footer.privacy': 'Политика за поверителност',
    'footer.terms': 'Условия за ползване',
    'footer.contact': 'Свържете се с нас',
    'footer.location': 'Пловдив, България',
    'footer.copyright': 'Всички права запазени.',
    'footer.privacyShort': 'Поверителност',
    'footer.termsShort': 'Условия',
    'footer.cookies': 'Бисквитки',
    // P0 pre-launch — real trust signals only (BG)
    'footer.trust.iata': 'Сравни полети и хотели при доставчиците',
    'footer.trust.ssl': 'Резервацията и плащането са при доставчика',
    'footer.trust.gdpr': 'TravelAIBG не приема плащания с карти',
    'footer.localeMarker': 'Цени в EUR · летищата зависят от търсенето',
    'footer.legalEntity': 'TravelAIBG · България',

    // Auth gate (soft wall before booking)
    'authGate.title': 'Запази плановете си',
    'authGate.subtitle': 'Влез, за да запазваш и разглеждаш отново плановете си. Можеш да отваряш търсения при доставчиците без профил.',
    'authGate.titleSignup': 'Създайте безплатен акаунт',
    'authGate.subtitleSignup': 'Съхранявай плановете и предпочитанията си на едно място. Резервацията и плащането остават при доставчика.',
    'authGate.titleSignin': 'Добре дошли отново',
    'authGate.subtitleSignin': 'Влез, за да видиш запазените си планове.',
    'authGate.benefitSave': 'Запазете моментално пътувания, хотели и полети',
    'authGate.benefitAlerts': 'Получавайте лични известия при намаление на цените',
    'authGate.benefitSecure': 'Сигурно, без спам — само едно докосване с Google',
    'authGate.continueWithGoogle': 'Продължи с Google',
    'authGate.googleReassurance': 'Ще ви прехвърлим към защитения вход на Google. Ние никога не виждаме и не съхраняваме паролата ви.',
    'authGate.googleBenefit1': 'С едно докосване — без нова парола за запомняне',
    'authGate.googleBenefit2': 'Получаваме само името, имейла и снимката ви от профила',
    'authGate.googleBenefit3': 'Можете да оттеглите достъпа по всяко време от Google акаунта си',
    'authGate.oauthBadge': 'Стандартен OAuth 2.0 · Използван от милиони',
    'authGate.continueWithFacebook': 'Продължи с Facebook',
    'authGate.or': 'или с имейл',
    'authGate.name': 'Име',
    'authGate.namePlaceholder': 'Вашето име',
    'authGate.email': 'Имейл',
    'authGate.emailPlaceholder': 'вие@example.com',
    'authGate.password': 'Парола',
    'authGate.createAccount': 'Създай акаунт',
    'authGate.signIn': 'Вход',
    // P0 trust row (BG)
    'authGate.trust.ssl': 'SSL защитено',
    'authGate.trust.gdpr': 'Налична информация за поверителността',
    'authGate.trust.noCard': 'Без карта',
    'authGate.haveAccount': 'Вече имате акаунт?',
    'authGate.noAccount': 'Нямате акаунт?',
    'authGate.fillFields': 'Моля, попълнете всички полета.',
    'authGate.passwordTooShort': 'Паролата трябва да е поне 6 символа.',
    'authGate.genericError': 'Нещо се обърка. Моля, опитайте отново.',
    'authGate.fbComingSoon': 'Facebook вход скоро ще е наличен. Моля, използвайте Google или имейл засега.',
    'authGate.terms': 'Като продължите, вие приемате нашите Условия и Политика за поверителност.',
    'authGate.forgotPassword': 'Забравена парола?',
    'authGate.titleForgot': 'Възстановете паролата',
    'authGate.subtitleForgot': 'Въведете имейла си и ще ви изпратим линк за нова парола.',
    'authGate.titleForgotSent': 'Проверете пощата си',
    'authGate.subtitleForgotSent': 'Линкът за възстановяване е на път',
    'authGate.forgotSentBody': 'Току-що изпратихме линк за възстановяване на пощата ви. Изтича след 60 минути. Ако не виждате имейла, проверете папката Spam.',
    'authGate.sendResetLink': 'Изпрати линк',
    'authGate.backToSignIn': 'Назад към вход',
    'authGate.rememberMe': 'Запомни ме',
    'authGate.rememberMeHint': 'Останете вписани на това устройство за 30 дни',
    'authGate.pwdStrength.weak': 'Слаба — добавете още символи',
    'authGate.pwdStrength.fair': 'Средна — добавете цифра или символ',
    'authGate.pwdStrength.good': 'Добра парола',
    'authGate.pwdStrength.strong': 'Силна парола',
    'hotel.nearby': 'Наблизо',
    'hotel.estimate': 'AI пример · реални цени при клик',
    'hotel.estimateTooltip': 'Това е AI пример. Реалната наличност и цена се зареждат при натискане на Резервирай.',
    'chat.hotelBookNoCity': 'Не мога да отворя резервация без дестинация. Опитайте ново търсене.',
    // Profile completion modal
    'profileGate.title': 'Почти готово — една бърза подробност',
    'profileGate.subtitle': 'Можеш да актуализираш контактите в профила си. Телефон не е необходим за отваряне на търсене при външен доставчик.',
    'profileGate.phoneLabel': 'Телефон (с код на държава)',
    'profileGate.phonePlaceholder': '+359 88 123 4567',
    'profileGate.countryLabel': 'Държава',
    'profileGate.countryPlaceholder': 'България',
    'profileGate.continueBtn': 'Запази данните в профила',
    'profileGate.skipBtn': 'Отказ',
    'profileGate.error.phone': 'Моля въведете валиден телефон (мин. 4 цифри).',
    'profileGate.benefitFasterBooking': 'Поддържай актуални данните в профила',
    'profileGate.benefitAlerts': 'Получавайте незабавни известия за намалени цени',
    'profileGate.benefitTrust': 'Никога не споделяме номера ви',
    // Admin inbox
    'admin.inbox.title': 'Входяща поща',
    'admin.inbox.subtitle': 'Съобщения от формата за контакт',
    'admin.inbox.unread': '{n} непрочетени',
    'admin.inbox.empty': 'Все още няма съобщения. Проверете по-късно.',
    'admin.inbox.markAllRead': 'Маркирай всички като прочетени',
    'admin.inbox.refresh': 'Опресни',
    'admin.inbox.toggleUnread': 'Показвай само непрочетени',
    'admin.inbox.from': 'От',
    'admin.inbox.subject': 'Тема',
    'admin.inbox.received': 'Получено',
    'admin.inbox.delivered': 'Имейлът е изпратен',
    'admin.inbox.pendingDelivery': 'Имейлът чака (запазен в БД)',
    'admin.inbox.replyCta': 'Отговори чрез имейл',
    'admin.inbox.forbidden': 'Нямате достъп до тази страница.',
    'admin.inbox.loading': 'Зареждане на съобщения…',
    'reset.title': 'Изберете нова парола',
    'reset.subtitle': 'Минимум 6 символа.',
    'reset.newPassword': 'Нова парола',
    'reset.confirmPassword': 'Потвърдете новата парола',
    'reset.placeholder': 'Нова парола',
    'reset.confirmPlaceholder': 'Повторете новата парола',
    'reset.submit': 'Запази паролата',
    'reset.minLength': 'Паролата трябва да е поне 6 символа',
    'reset.mismatch': 'Паролите не съвпадат',
    'reset.invalidToken': 'Линкът за възстановяване липсва или е невалиден. Моля, поискайте нов.',
    'reset.genericError': 'Възникна грешка. Моля, опитайте отново.',
    'reset.successTitle': 'Паролата е обновена',
    'reset.successBody': 'Вие сте влезли. Време е за следващото пътуване.',
    'reset.continueCta': 'Към TravelAI',

    // Auth callback (post-Google return page)
    'authCallback.signingIn': 'Влизане в профила…',
    'authCallback.almostThere': 'Почти готово.',
    'authCallback.welcome': 'Добре дошли!',
    'authCallback.taking': 'Връщаме ви в TravelAI…',
    'authCallback.failed': 'Влизането се провали',
    'authCallback.backHome': 'Обратно към началото',

    // Packages page
    'packages.headerBadge': 'Селектирани от нашите експерти',
    'packages.title': 'Селектирани пакетни оферти',
    'packages.subtitle': 'Подбрани преживявания с тръгване от София за всеки тип пътешественик — от уикенд бягства до пътувания веднъж в живота.',
    'packages.viewDetails': 'Виж детайли',
    'packages.popularDestinations': 'Популярни дестинации',
    // Per-package localised copy
    'packages.pkg-budget.name': 'Балканско икономично бягство',
    'packages.budgetDesc': '5-дневна почивка от София до близки бижута',
    'packages.budgetLong': 'Открийте богатата култура и пленителните пейзажи на Балканите с тази достъпна 5-дневна екскурзия. Разгледайте историческите Атина, насладете се на егейския бряг на Солун и потопете се в живия дух на Белград — всичко това от само 299 €.',
    'packages.budgetH1': 'Обиколка с екскурзовод на Акропола и Партенона',
    'packages.budgetH2': 'Круиз по залез от Пирея',
    'packages.budgetH3': 'Вечеря в традиционна таверна',
    'packages.budgetH4': 'Еднодневна екскурзия до нос Сунион',
    'packages.budgetH5': 'Свободно време в оживения Монастираки',
    'packages.pkg-family.name': 'Средиземноморско семейно приключение',
    'packages.familyDesc': '7 дни от София — слънце, море и култура',
    'packages.familyLong': '7-дневно средиземноморско приключение, създадено за семейства. Разходете се по историческите улички на Барселона, удивете се на римския Колизеум и се насладете на италианското слънце в Неапол. Включва семейни хотели и удобни връзки.',
    'packages.familyH1': 'Обиколка на Саграда Фамилия и Парк Гюел',
    'packages.familyH2': 'Плажен ден в Барселонета',
    'packages.familyH3': 'Пешеходна обиколка на Готическия квартал',
    'packages.familyH4': 'Семейна вечер с тапас',
    'packages.familyH5': 'Еднодневна екскурзия до Монсерат',
    'packages.pkg-luxury.name': 'Луксозно средиземноморско бягство',
    'packages.luxuryDesc': '10-дневно премиум преживяване от София',
    'packages.luxuryLong': '10-дневно премиум преживяване за пътешественици, които търсят най-доброто. Започнете в София, отлетете към вулканичната красота на Санторини, забавлявайте се в Миконос и завършете с футуристичния лукс на Дубай. 5-звездни хотели, бизнес класа и частни трансфери през цялото пътуване.',
    'packages.luxuryH1': 'Частен круиз при залез в калдерата',
    'packages.luxuryH2': 'Дегустация в Santo Wines',
    'packages.luxuryH3': 'Хеликоптерен трансфер до Миконос',
    'packages.luxuryH4': 'Обяд на плажа в Nammos',
    'packages.luxuryH5': 'VIP площадка на Бурж Халифа',
    // Popular destinations strip
    'packages.dest.paris.name': 'Париж',
    'packages.dest.paris.country': 'Франция',
    'packages.dest.barcelona.name': 'Барселона',
    'packages.dest.barcelona.country': 'Испания',
    'packages.dest.rome.name': 'Рим',
    'packages.dest.rome.country': 'Италия',
    'packages.dest.amsterdam.name': 'Амстердам',
    'packages.dest.amsterdam.country': 'Нидерландия',
    'packages.dest.mykonos.name': 'Миконос',
    'packages.dest.mykonos.country': 'Гърция',
    'packages.dest.dubai.name': 'Дубай',
    'packages.dest.dubai.country': 'ОАЕ',
    'packages.dest.naples.name': 'Неапол',
    'packages.dest.naples.country': 'Италия',
    'packages.dest.thessaloniki.name': 'Солун',
    'packages.dest.thessaloniki.country': 'Гърция',
    'packages.dest.belgrade.name': 'Белград',
    'packages.dest.belgrade.country': 'Сърбия',

    // Saved page
    'saved.title': 'Запазени',
    'saved.subtitle': 'Вашите запазени пътувания, полети и хотели',
    'saved.emptyTitle': 'Влезте, за да видите запазените',
    'saved.emptyDescription': 'Запазвайте пътувания, сравнявайте опции и продължавайте точно откъдето сте спрели — на всяко устройство.',
    'saved.signInCta': 'Влезте, за да продължите',

    // Profile page
    'profile.title': 'Моят профил',
    'profile.subtitle': 'Управлявайте акаунта и предпочитанията си',
    'profile.emptyTitle': 'Влезте, за да видите профила си',
    'profile.emptyDescription': 'Преглед на резервации, управление на предпочитания и персонализиран емисия с пътувания.',
    'profile.signInCta': 'Вход',

    // ===== СТАТИЧНИ СТРАНИЦИ ОТ ФУТЪРА =====
    'common.back': 'Назад към началото',
    'page.lastUpdated': 'Последна актуализация',

    // Помощен център
    'page.help.title': 'Помощен център',
    'page.help.lead': 'Имате нужда от помощ? На правилното място сте. По-долу са отговорите на най-често задаваните въпроси. Ако нещо липсва, пишете ни от страницата „Свържете се с нас" и ще ви отговорим в рамките на един работен ден.',
    'page.help.section.1.heading': 'Как работи TravelAI?',
    'page.help.section.1.body': 'TravelAI е базиран на изкуствен интелект пътен консиерж. Кажете в чата къде искате да отидете (или просто опишете какъв тип пътуване търсите — „уикенд в голям град под 500€", „семейна морска почивка през юли") и веднага ще ви изградим пълно пътуване със самолетни билети, хотели, дневен план и оценка на цената. Когато натиснете „Резервирай", ви пренасочваме към сайта на партньора (Aviasales, Booking.com, Hotels.com и т.н.), където се извършва самата покупка — сигурно, на касата на партньора.',
    'page.help.section.2.heading': 'Продавате ли директно полети и хотели?',
    'page.help.section.2.body': 'Не. Ние сме афилиейт агрегатор — показваме най-добрите оферти от нашите партньори и получаваме малка комисиона, когато резервирате чрез нас. Тази комисиона не ви струва нищо допълнително; цената, която виждате на страницата на партньора, е точно това, което плащате. Резервацията ви, парите ви и поддръжката ви се поддържат директно от партньора (авиокомпанията, хотелската верига или Booking.com / Aviasales).',
    'page.help.section.3.heading': 'Истински ли са цените, които виждам?',
    'page.help.section.3.body': 'Маркираме всяка цена ясно с един от трите етикета:\n• „Live" (зелена точка) — кеширана цена от партньорско API, точна към момента на извличане.\n• „Est." или „~€X" — приблизителен диапазон, базиран на исторически тарифи за този маршрут или хотелска категория.\n• „From €X" — промоционална стартова цена за наши пакети.\nКрайната потвърдена цена винаги е тази, която партньорът показва на касата си. Никога не таксуваме ние самите.',
    'page.help.section.4.heading': 'Как да запазя пътувания и да получавам ценови предупреждения?',
    'page.help.section.4.body': 'Създайте безплатен акаунт (вход с Google с едно докосване или имейл/парола). След като сте влезли, всяко пътуване, което изградите в AI чата, може да бъде запазено в таблото ви. От всеки полетен резултат можете също да докоснете иконата на камбанка, за да се абонирате за ценови предупреждения — ще ви изпратим имейл в момента, в който цената падне под прага ви.',
    'page.help.section.5.heading': 'Потвърждението ми за резервация не пристигна — какво да правя?',
    'page.help.section.5.body': 'Потвърждението идва от партньора, при който сте резервирали (Booking.com, авиокомпанията и т.н.), а НЕ от TravelAI. Проверете входящата кутия на имейла, който сте използвали на тяхната каса (и папката със спам). Ако сте използвали псевдоним или сте написали грешен адрес, свържете се директно с обслужването на клиенти на партньора — телефонът и имейлът им обикновено са в описанието на банковото ви извлечение.',
    'page.help.section.6.heading': 'Все още имате проблем?',
    'page.help.section.6.body': 'Пишете ни на travelaibg@gmail.com или използвайте формата „Свържете се с нас". Отговаряме на всяко съобщение — обикновено в рамките на няколко часа в европейско работно време.',

    // Често задавани въпроси
    'page.faq.title': 'Често задавани въпроси',
    'page.faq.lead': 'Кратки отговори на най-често задаваните въпроси относно TravelAI.',
    'page.faq.section.1.heading': 'Безплатно ли е използването на TravelAI?',
    'page.faq.section.1.body': 'Да. Изграждането на пътувания, разглеждането на полети и хотели, получаването на ценови предупреждения — всичко е напълно безплатно за вас. Печелим малка афилиейт комисиона от партньорите си, когато резервирате, платена от партньора, а не добавена към вашата цена.',
    'page.faq.section.2.heading': 'Нужен ли ми е акаунт?',
    'page.faq.section.2.body': 'Можете да разглеждате и да чатите без такъв. Имате нужда от акаунт само когато искате да запазите пътуване, да се абонирате за ценови предупреждения или да завършите резервация — за да можем да ви изпратим потвърждение по имейл.',
    'page.faq.section.3.heading': 'Кои валути поддържате?',
    'page.faq.section.3.body': 'Цените се показват по подразбиране в евро. Сайтовете на партньорите ще покажат местна валута на касата въз основа на вашия IP / езикови настройки на браузъра. Винаги се таксувате във валутата на касата на партньора.',
    'page.faq.section.4.heading': 'Мога ли да резервирам от градове, различни от София?',
    'page.faq.section.4.body': 'Да. Използвайте глобалната лента за търсене в горната част на която и да е страница, за да зададете различен град на заминаване. Каталогът ни обхваща 300+ летища по целия свят. AI чатът също приема свободни заявки като „полети от Виена до Бали".',
    'page.faq.section.5.heading': 'Какво става, ако избраният от мен град няма летище?',
    'page.faq.section.5.body': 'Автоматично ви насочваме към най-близкото комерсиално летище с ясна подсказка „Обслужва се от …". Например, ако поискате Марбея, ще ви покажем полети до Малага (AGP, 45 мин с кола), а Халщат се обслужва от Залцбург (SZG).',
    'page.faq.section.6.heading': 'Колко точни са датите, предложени от AI?',
    'page.faq.section.6.body': 'Когато не посочите дати, AI избира следващия възможен прозорец, започващ поне 14 дни от днес (никога минали дати). За заявки „уикенд" избира 2-3 нощувки петък-неделя. Винаги можете да заместите това, като въведете точни дати в чата.',
    'page.faq.section.7.heading': 'Мога ли да отменя или променя резервацията си?',
    'page.faq.section.7.body': 'Политиките за отмяна и промяна са изцяло на партньора — моля, прочетете ги на страницата им на касата преди да платите. Гъвкавите тарифи на Booking.com обикновено могат да се откажат безплатно до няколко дни преди настаняване; авиобилетите варират значително според тарифния клас.',
    'page.faq.section.8.heading': 'Обработвате ли групови резервации?',
    'page.faq.section.8.body': 'За до 9 пътници на резервация партньорите обикновено се справят чрез нормалния си процес. За по-големи групи (училищни екскурзии, сватби, корпоративни събития) пишете ни на travelaibg@gmail.com — можем да помогнем да договорим оферта.',
    'page.faq.section.9.heading': 'Сигурни ли са данните ми?',
    'page.faq.section.9.body': 'Да. Съхраняваме само това, от което имаме нужда (имейл, хеш на парола, запазени пътувания, предупреждения). Плащанията отиват директно при партньорите — никога не виждаме данните на картата ви. Пълни детайли в Политиката за поверителност.',
    'page.faq.section.10.heading': 'Как мога да докладвам грешка или да предложа функционалност?',
    'page.faq.section.10.body': 'Изпратете имейл на travelaibg@gmail.com или използвайте формата „Свържете се с нас". Четем всяко съобщение — вашата обратна връзка оформя нашата пътна карта.',

    // Политика за поверителност
    'page.privacy.title': 'Политика за поверителност',
    'page.privacy.lead': 'TravelAI („ние", „нас") уважава вашата поверителност и се ангажира да защитава личните ви данни в съответствие с Общия регламент за защита на данните на ЕС (GDPR) и българския Закон за защита на личните данни. Тази политика обяснява какво събираме, защо го събираме и какви права имате.',
    'page.privacy.section.1.heading': '1. Кои сме ние',
    'page.privacy.section.1.body': 'TravelAI е базиран на AI пътен консиерж, който ви помага да откриете и резервирате полети, хотели и пакети чрез нашите афилиейт партньори. Управляваме уебсайта travelaibg.com. Имейлът ни за връзка е travelaibg@gmail.com.',
    'page.privacy.section.2.heading': '2. Какви данни събираме',
    'page.privacy.section.2.body': 'Събираме:\n• Данни за акаунт — имейл адрес и (за акаунти с парола) солен bcrypt хеш на паролата ви. Ако влезете с Google, получаваме името, имейла и профилната ви снимка от Google.\n• Данни за пътуване — дестинации, дати, брой пътници и други параметри, които въвеждате в AI чата или запазвате в таблото си.\n• Абонаменти за ценови предупреждения — IATA на произхода/дестинацията, прагова цена, език.\n• Технически данни — IP адрес, тип браузър, устройство, език, препращащ URL. Използвани за анализи и предотвратяване на злоупотреби.\nНЕ събираме данни за платежни карти. Всички резервации се извършват на касата на партньора — никога не виждаме картата ви.',
    'page.privacy.section.3.heading': '3. Защо ги събираме (правни основания)',
    'page.privacy.section.3.body': 'Обработваме данните на три основания по GDPR:\n• Договор — за да предоставяме услугата, която поискахте (запазване на пътувания, изпращане на предупреждения, доставяне на потвърждения).\n• Легитимен интерес — за подобряване на продукта, предотвратяване на измами и управление на уебсайта.\n• Съгласие — за маркетингови имейли, където е приложимо. Можете да оттеглите съгласието си по всяко време.',
    'page.privacy.section.4.heading': '4. С кого ги споделяме',
    'page.privacy.section.4.body': 'Споделяме данни само с процесорите, необходими за функционирането на услугата:\n• Brevo (Sendinblue) — доставка на транзакционни имейли.\n• Travelpayouts / Aviasales — полетни метаданни и афилиейт проследяване.\n• Booking.com, Hotels.com, Agoda — когато преминете към резервация на хотели.\n• MongoDB Atlas — хостинг на база данни.\n• Emergent / Google — удостоверяване (само ако използвате Google вход).\nВсички процесори са в съответствие с GDPR и обвързани със Споразумения за обработка на данни.',
    'page.privacy.section.5.heading': '5. Колко дълго ги съхраняваме',
    'page.privacy.section.5.body': 'Данни за акаунт: докато акаунтът ви е активен. Можете да изтриете акаунта си по всяко време — премахваме трайно личните ви данни в рамките на 30 дни (някои анонимизирани логове могат да останат за предотвратяване на измами до 12 месеца). Запазени пътувания и предупреждения: докато не ги изтриете или закриете акаунта си.',
    'page.privacy.section.6.heading': '6. Вашите права по GDPR',
    'page.privacy.section.6.body': 'Имате право на: (а) достъп до данните си, (б) корекция, (в) изтриване, (г) ограничаване на обработката, (д) преносимост на данните, (е) възражение срещу обработката, (ж) подаване на жалба до Комисията за защита на личните данни (КЗЛД, www.cpdp.bg). За да упражните което и да е от тези права, пишете на travelaibg@gmail.com.',
    'page.privacy.section.7.heading': '7. Бисквитки и проследяване',
    'page.privacy.section.7.body': 'Използваме малък брой основни бисквитки (сесия, езикова настройка, A/B вариант). Не използваме рекламни проследяващи бисквитки на трети страни. Афилиейт партньорите могат да задават свои бисквитки, след като преминете към техния сайт — прегледайте техните политики за бисквитки.',
    'page.privacy.section.8.heading': '8. Промени в тази политика',
    'page.privacy.section.8.body': 'Можем периодично да актуализираме тази политика. Датата „Последна актуализация" в долната част винаги отразява най-новата версия. Значителни промени ще бъдат обявявани по имейл на активните потребители.',

    // Условия за ползване
    'page.terms.title': 'Условия за ползване',
    'page.terms.lead': 'Като използвате TravelAI (travelaibg.com), вие се съгласявате с тези условия. Моля, прочетете ги внимателно — те включват важни права, задължения и ограничения.',
    'page.terms.section.1.heading': '1. Какво е TravelAI',
    'page.terms.section.1.body': 'TravelAI е информационна и откривателска услуга. Помагаме ви да планирате пътувания, да намирате полети и хотели и да сравнявате цени чрез афилиейт партньори. НЕ сме туристически агент, авиокомпания или хотел — не продаваме, не издаваме и не оперираме никоя от показаните услуги. Всички действителни резервации, плащания и договори между пътника и доставчика се извършват на сайта на партньора при условията на партньора.',
    'page.terms.section.2.heading': '2. Допустимост',
    'page.terms.section.2.body': 'Трябва да сте поне на 18 години, за да създадете акаунт или да направите резервация чрез нашите партньори. Като използвате услугата, потвърждавате, че сте.',
    'page.terms.section.3.heading': '3. Вашият акаунт',
    'page.terms.section.3.body': 'Отговорни сте за запазването на сигурността на паролата си и за всяка активност под акаунта ви. Уведомете ни незабавно, ако подозирате неоторизиран достъп. Можем да спрем акаунти, които нарушават тези условия или приложимия закон.',
    'page.terms.section.4.heading': '4. Цени и наличност',
    'page.terms.section.4.body': 'Цените и наличността в TravelAI идват от API на трети страни и подбрани данни. Те са приблизителни — крайните, обвързващи цени се появяват на касата на партньора в реално време. Не можем да гарантираме, че цена, видяна в TravelAI, ще бъде все още налична на сайта на партньора. Не носим отговорност за разлики в цените, изчерпани наличности или грешки от страна на партньора.',
    'page.terms.section.5.heading': '5. Резервации и отмени',
    'page.terms.section.5.body': 'Всяка резервация е договор между вас и партньора (авиокомпанията, хотела, OTA), уреден от техните условия и политика за отмяна. Прочетете ги преди да платите. Не обработваме възстановявания, промени или искове от името на партньора — моля, свържете се директно с партньора.',
    'page.terms.section.6.heading': '6. Допустимо използване',
    'page.terms.section.6.body': 'Не използвайте TravelAI за: (а) извличане или препубликуване на съдържание без разрешение, (б) подаване на фалшиви или измамни резервации, (в) злоупотреба с нашия AI чат (спам, prompt injection, опити за извличане на собствени промптове) или (г) нарушаване на приложимия закон. Можем да ограничим или забраним акаунти, които правят това.',
    'page.terms.section.7.heading': '7. Ограничение на отговорността',
    'page.terms.section.7.body': 'TravelAI се предоставя „както е". До максималната степен, разрешена от българското и европейското право, не носим отговорност за непреки, последващи или наказателни щети, пропуснати пътувания, изпуснати полети, грешки от страна на партньора или каквато и да е загуба, надвишаваща размера на афилиейт комисионата, която сме спечелили от вашата конкретна резервация (обикновено няколко евро). Нищо в тези условия не ограничава отговорността за измама, умишлено неправомерно поведение или права, които имате по силата на задължителното потребителско право.',
    'page.terms.section.8.heading': '8. Интелектуална собственост',
    'page.terms.section.8.body': 'Името, логото, AI промптовете, кодът и подбраното съдържание на TravelAI са нашата интелектуална собственост. Имената на марки на партньори (Booking.com, Aviasales, авиокомпании, хотели) принадлежат на съответните им собственици. Не можете да възпроизвеждате или комерсиално експлоатирате нашето съдържание без писмено разрешение.',
    'page.terms.section.9.heading': '9. Приложимо право',
    'page.terms.section.9.body': 'Тези условия се уреждат от българското право. Всеки спор, произтичащ от или във връзка с услугата, ще бъде разрешен от компетентните съдилища на България, без да се засягат задължителните защити на потребителите по правото на държавата на пребиваване.',

    // Страница за контакти
    'contact.title': 'Свържете се с нас',
    'contact.lead': 'Имате въпрос, намерили сте грешка или искате да предложите функционалност? Пишете ни и ще ви отговорим в рамките на един работен ден.',
    'contact.emailLabel': 'Имейл',
    'contact.locationLabel': 'Къде се намираме',
    'contact.responseTime': 'Обикновено отговаряме в рамките на няколко часа в европейско работно време. Извън работно време очаквайте отговор на следващата сутрин.',
    'contact.fieldName': 'Вашето име',
    'contact.fieldNamePlaceholder': 'Иван Иванов',
    'contact.fieldEmail': 'Имейл адрес',
    'contact.fieldSubject': 'Тема',
    'contact.fieldSubjectPlaceholder': 'Как можем да помогнем?',
    'contact.fieldMessage': 'Съобщение',
    'contact.fieldMessagePlaceholder': 'Разкажете ни малко повече за това от какво се нуждаете…',
    'contact.privacy': 'С изпращането се съгласявате да използваме горната информация, за да отговорим на съобщението ви. Вижте нашата Политика за поверителност за подробности.',
    'contact.send': 'Изпрати съобщение',
    'contact.sending': 'Изпраща се…',
    'contact.successTitle': 'Съобщението е изпратено!',
    'contact.successMessage': 'Благодарим — получихме съобщението ви и ще отговорим скоро.',
    'contact.errorTitle': 'Съобщението не можа да бъде изпратено',
    'contact.errorGeneric': 'Нещо се обърка. Моля, опитайте отново или ни пишете директно:',
    'contact.errorNetwork': 'Мрежова грешка. Моля, проверете връзката си и опитайте отново или ни пишете директно:',
    'contact.emailFallback': 'travelaibg@gmail.com',
    'contact.validation': 'Моля, попълнете всички задължителни полета с валиден имейл и съобщение с поне 5 символа.',
  },
};

const LanguageContext = createContext(undefined);

// Country to language mapping
const countryLanguageMap = {
  'BG': 'bg', 'DE': 'de', 'ES': 'es', 'FR': 'fr', 'IT': 'it',
  'GB': 'en', 'US': 'en', 'GR': 'el', 'NL': 'nl', 'AT': 'de',
  'CH': 'de', 'PT': 'pt', 'RO': 'ro', 'PL': 'pl', 'CZ': 'cs',
  'HU': 'hu', 'TR': 'tr', 'AE': 'ar'
};

// Country -> nearest major airport IATA code (for AI departure default)
const countryAirportMap = {
  'BG': 'SOF', 'RO': 'OTP', 'GR': 'ATH', 'TR': 'IST', 'RS': 'BEG',
  'MK': 'SKP', 'AL': 'TIA', 'HR': 'ZAG', 'HU': 'BUD', 'AT': 'VIE',
  'CZ': 'PRG', 'PL': 'WAW', 'DE': 'BER', 'FR': 'CDG', 'IT': 'FCO',
  'ES': 'MAD', 'PT': 'LIS', 'GB': 'LHR', 'IE': 'DUB', 'NL': 'AMS',
  'BE': 'BRU', 'CH': 'ZRH', 'US': 'JFK', 'AE': 'DXB',
};

// Detect user's country, language, and nearest airport in one shot
const detectUserContext = async () => {
  try {
    const browserLang = navigator.language.split('-')[0];
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      const country = data.country_code || 'BG';
      const detectedLang = countryLanguageMap[country];

      // Prefer detected country language, then browser language, then English.
      let preferredLang = 'en';
      if (detectedLang && translations[detectedLang]) {
        preferredLang = detectedLang;
      } else if (translations[browserLang]) {
        preferredLang = browserLang;
      }

      return {
        language: preferredLang,
        country,
        nearestAirport: countryAirportMap[country] || 'SOF',
      };
    }
    return {
      language: translations[browserLang] ? browserLang : 'en',
      country: 'BG',
      nearestAirport: 'SOF',
    };
  } catch {
    const browserLang = navigator.language.split('-')[0];
    return {
      language: translations[browserLang] ? browserLang : 'en',
      country: 'BG',
      nearestAirport: 'SOF',
    };
  }
};

// Backwards-compatible language-only detector (kept for any external callers)
// eslint-disable-next-line no-unused-vars
const detectUserLanguage = async () => {
  const ctx = await detectUserContext();
  return ctx.language;
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('en');
  const [country, setCountry] = useState('BG');
  const [nearestAirport, setNearestAirport] = useState('SOF');
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  useEffect(() => {
    const initLanguage = async () => {
      const saved = localStorage.getItem('userLanguage');
      // Explicit hreflang landing URLs must override geo/IP and saved locale.
      const requested = new URLSearchParams(window.location.search).get('lang');
      const ctx = await detectUserContext();
      setCountry(ctx.country);
      setNearestAirport(ctx.nearestAirport);
      if (requested === 'bg' || requested === 'en') {
        setLanguage(requested);
        setIsAutoDetected(false);
      } else if (saved && translations[saved]) {
        setLanguage(saved);
      } else {
        setLanguage(ctx.language);
        setIsAutoDetected(true);
      }
    };
    initLanguage();
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    setIsAutoDetected(false);
    localStorage.setItem('userLanguage', lang);
    const query = new URLSearchParams(window.location.search);
    // Keep a language-specific public URL only for our advertised BG/EN variants.
    if (lang === 'bg' || lang === 'en') query.set('lang', lang);
    else query.delete('lang');
    window.history.replaceState({}, '', `${window.location.pathname}${query.toString() ? `?${query}` : ''}${window.location.hash}`);
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key, vars) => {
    const template = translations[language]?.[key] || translations['en']?.[key] || key;
    if (!vars || typeof template !== 'string') return template;
    return template.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
  }, [language]);

  const value = useMemo(() => ({
    language,
    country,
    nearestAirport,
    setLanguage: changeLanguage,
    t,
    languages,
    isAutoDetected
  }), [language, country, nearestAirport, isAutoDetected, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
